/* eslint-disable */
/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, jsx-a11y/alt-text, no-useless-computed-key */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from 'recharts';
import { CustomXAxisTick } from '../../components/CustomXAxisTick';
import '../../styles/components.css';
import CompanyFirstRun from "../CompanyFirstRun";
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
// @ts-ignore
import tblLogo from '../../tbl_logo.png';
import { useAuth } from '../../contexts/AuthContext';

interface Project {
  id: string;
  name: string;
  status: string;
  budget: number;
  ngo_id?: string;
  ngo?: { legal_name: string };
}

interface NGO {
  id: string;
  legal_name: string;
  status: string;
}

interface Metric {
  id: string;
  project_id: string;
  metric_name: string;
  target_value: number;
  current_value?: number;
  unit?: string | null;
  ngo_name?: string;
  ngo_ids?: string[];
}

const ScoreCard: React.FC<{ label: string; value: number | string; color?: string }> = ({ label, value, color = "#10b981" }) => (
  <div className="dashboard-scorecard">
    <div className="scorecard-content">
      <div className="scorecard-value" style={{ color }}>{value}</div>
      <div className="scorecard-label">{label}</div>
    </div>
  </div>
);

const CompanyDashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const companyId = userProfile?.company_id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [projects, setProjects] = useState<Project[]>([]);
  const [ngos, setNgos] = useState<NGO[]>([]);
  const [projectExpenditures, setProjectExpenditures] = useState<any[]>([]);
  const [complianceSnapshot, setComplianceSnapshot] = useState<{ pending: number; done: number }>({ pending: 0, done: 0 });
  const [companyCsrMandate, setCompanyCsrMandate] = useState<number>(0);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [companyName, setCompanyName] = useState<string>('');
  const [companyProfile, setCompanyProfile] = useState<any>(null);
  const [logoErrors, setLogoErrors] = useState<Record<string, boolean>>({});
  const [signedLogoUrls, setSignedLogoUrls] = useState<Record<string, string>>({});

  const [ngoDocs, setNgoDocs] = useState<any[]>([]);
  const [ngoUploads, setNgoUploads] = useState<any[]>([]);
  const [selectedComplianceNgo, setSelectedComplianceNgo] = useState<string>('all');
  const [selectedComplianceCompany, setSelectedComplianceCompany] = useState<string>('all');

  const [companyDocs, setCompanyDocs] = useState<any[]>([]);
  const [companyAssignments, setCompanyAssignments] = useState<any[]>([]);
  const [companyUploads, setCompanyUploads] = useState<any[]>([]);
  const [companyEntities, setCompanyEntities] = useState<any[]>([]);
  const [fundDisbursements, setFundDisbursements] = useState<any[]>([]);
  const [adminExpenses, setAdminExpenses] = useState<any[]>([]);
  const [foundationContributions, setFoundationContributions] = useState<any[]>([]);

  const [selectedImpactProject, setSelectedImpactProject] = useState<string>('all');
  const [selectedImpactNgo, setSelectedImpactNgo] = useState<string>('all');
  const [showAllFoundationDocs, setShowAllFoundationDocs] = useState(false);
  const [showAllEntityDocs, setShowAllEntityDocs] = useState(false);
  const [showAllNgoDocs, setShowAllNgoDocs] = useState(false);
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      setError('Company ID is missing');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // --- BATCH 1: Fetch Base Data in Parallel ---
      const [
        companyRes,
        projectsRes,
        ngosRes,
        companyDocDefsRes,
        ngoDocDefsRes,
        companyEntitiesRes,
        companyAssignmentsRes,
        companyAdminExpensesRes,
        foundationContributionsBatch1Res
      ] = await Promise.all([
        supabase.from('companies').select('*').eq('id', companyId).maybeSingle(),
        supabase.from('projects').select('id, name, status, budget').eq('company_id', companyId),
        supabase.from('ngos').select('id, legal_name, status, logo_url').eq('company_id', companyId),
        supabase.from('document_repository').select('*').eq('category', 'compliance').eq('is_active', true).order('display_order', { ascending: true }),
        supabase.from('document_repository').select('*').eq('category', 'compliance').eq('is_active', true).order('display_order', { ascending: true }),
        supabase.from('company_entities').select('*').eq('company_id', companyId),
        supabase.from('document_assignments').select('*').eq('company_id', companyId),
        supabase.from('company_admin_expenses').select('*').eq('company_id', companyId),
        supabase.from('foundation_contributions').select('*').eq('company_id', companyId)
      ]);

      if (projectsRes.error) throw projectsRes.error;
      if (ngosRes.error) throw ngosRes.error;

      // Process Batch 1 Data
      const entities = companyEntitiesRes.data || [];
      const totalMandate = entities.reduce((sum: number, e: any) => sum + (Number(e.csr_mandate) || 0), 0);
      setCompanyCsrMandate(totalMandate);
      setCompanyEntities(entities);

      if (companyRes.data) {
        setCompanyName(companyRes.data.name || '');
        setCompanyProfile(companyRes.data);
      }

      setAdminExpenses(companyAdminExpensesRes.data || []);

      const rawProjects = projectsRes.data || [];
      const rawNgos = ngosRes.data || [];
      const rawCompanyDocs = companyDocDefsRes.data || [];
      const companyDocIds = rawCompanyDocs.map((d: any) => d.id);
      const rawNgoDocs = ngoDocDefsRes.data || [];

      const rawAssignments = companyAssignmentsRes.data || [];

      // EXTRACT ALL NGO IDS (from direct links AND assignments)
      const directNgoIds = rawNgos.map((n: any) => n.id);
      const assignedNgoIds = Array.from(new Set(rawAssignments.map((a: any) => a.ngo_id).filter(Boolean) as string[]));
      const allNgoIds = Array.from(new Set([...directNgoIds, ...assignedNgoIds]));

      // If we have extra NGO IDs from assignments, fetch their info
      let finalNgos = rawNgos;
      const extraNgoIds = assignedNgoIds.filter(id => !rawNgos.find((n: any) => n.id === id));
      if (extraNgoIds.length > 0) {
        const { data: extraNgoData } = await supabase.from('ngos').select('id, legal_name, status, logo_url').in('id', extraNgoIds);
        if (extraNgoData) finalNgos = [...finalNgos, ...extraNgoData];
      }

      // For compliance tracking, only show verified NGOs.
      // Unverified NGOs (invited/submitted/rejected) should not appear in the compliance gauges.
      const verifiedNgos = finalNgos.filter((n: any) => n.status === 'verified');
      setNgos(verifiedNgos);
      const allRepoDocs = companyDocDefsRes.data || [];

      // --- Augment Assignments with Auto-Generated logic (Sync with CompanyCompliance.tsx) ---
      const augmentedAssignments = [...rawAssignments];
      allRepoDocs.forEach((doc: any) => {
        if (!doc.role) return;
        const docRole = doc.role.toLowerCase();

        // Auto-assign to NGOs
        if (docRole === 'ngo') {
          finalNgos.forEach((ngo: any) => {
            if (!augmentedAssignments.find(a => a.document_repository_id === doc.id && a.ngo_id === ngo.id)) {
              augmentedAssignments.push({
                id: `auto-ngo-${doc.id}-${ngo.id}`,
                document_repository_id: doc.id,
                company_id: companyId,
                ngo_id: ngo.id,
                status: 'Pending',
                is_auto: true
              });
            }
          });
        }
        // Auto-assign to Foundation (Corporate)
        else if (docRole === 'foundation') {
          // Foundation docs should only go to companies with foundations, not standard single corporates
          if (companyRes.data?.setup_type !== 'single') {
            if (!augmentedAssignments.find(a => a.document_repository_id === doc.id && !a.ngo_id && !a.entity_id)) {
              augmentedAssignments.push({
                id: `auto-fnd-${doc.id}`,
                document_repository_id: doc.id,
                company_id: companyId,
                ngo_id: null,
                entity_id: null,
                status: 'Pending',
                is_auto: true
              });
            }
          }
        }
        // Auto-assign to Entities (Business Units)
        else if (docRole === 'company') {
          if (companyRes.data?.setup_type === 'single') {
            // For a single company with no entities, company docs go to the head office
            if (!augmentedAssignments.find(a => a.document_repository_id === doc.id && !a.ngo_id && !a.entity_id)) {
              augmentedAssignments.push({
                id: `auto-ent-${doc.id}-single`,
                document_repository_id: doc.id,
                company_id: companyId,
                ngo_id: null,
                entity_id: null,
                status: 'Pending',
                is_auto: true
              });
            }
          } else {
            entities.forEach((ent: any) => {
              if (!augmentedAssignments.find(a => a.document_repository_id === doc.id && a.entity_id === ent.id)) {
                augmentedAssignments.push({
                  id: `auto-ent-${doc.id}-${ent.id}`,
                  document_repository_id: doc.id,
                  company_id: companyId,
                  ngo_id: null,
                  entity_id: ent.id,
                  status: 'Pending',
                  is_auto: true
                });
              }
            });
          }
        }
      });

      setCompanyAssignments(augmentedAssignments);

      const allProjectIds = rawProjects.map((p: any) => p.id);
      setCompanyDocs(rawCompanyDocs);
      setNgoDocs(rawNgoDocs);


      // --- BATCH 2: Fetch Dependent Data in Parallel ---
      const queries = [];

      // 1. Project NGO Mappings
      queries.push(
        allProjectIds.length > 0
          ? supabase.from('project_ngos').select('project_id, ngo_id, status').in('project_id', allProjectIds)
          : Promise.resolve({ data: [], error: null })
      );

      // 2. Metrics (Base definitions for targets)
      queries.push(
        allProjectIds.length > 0
          ? supabase.from('project_metrics').select('id, project_id, metric_name, target_value, unit').in('project_id', allProjectIds)
          : Promise.resolve({ data: [], error: null })
      );

      // 2.5 Metrics Progress (For achieved values)
      queries.push(
        allProjectIds.length > 0
          ? supabase.from('project_metrics_progress').select('project_id, metric_name, current_value').in('project_id', allProjectIds)
          : Promise.resolve({ data: [], error: null })
      );

      // 3. Expenditures
      queries.push(
        allProjectIds.length > 0
          ? supabase.from('project_expenditures').select('id, project_id, expenditure_amount, description').in('project_id', allProjectIds)
          : Promise.resolve({ data: [], error: null })
      );

      // 3.5 Fund Disbursements (Source of truth for Mandate fulfillment)
      queries.push(
        allProjectIds.length > 0
          ? supabase.from('fund_disbursements').select('*').in('project_id', allProjectIds)
          : Promise.resolve({ data: [], error: null })
      );

      // 4. Company Compliance Uploads
      queries.push(
        companyDocIds.length > 0
          ? supabase.from('document_uploads').select('*').eq('company_id', companyId).in('document_repository_id', companyDocIds).eq('status', 'active')
          : Promise.resolve({ data: [], error: null })
      );

      // 4b. Submissions are already handled by queries 4 and 5

      // 5. NGO Compliance Uploads (Use allNgoIds)
      queries.push(
        allNgoIds.length > 0
          ? supabase.from('document_uploads').select('*').in('ngo_id', allNgoIds).eq('status', 'active')
          : Promise.resolve({ data: [], error: null })
      );

      // 6. Foundation Contributions (Source of truth for ADMF funding)
      // Already fetched in Batch 1, but if we wanted to refresh or wait:
      // queries.push(supabase.from('foundation_contributions').select('*').eq('company_id', companyId));

      const [
        projectNgosRes,
        metricsDefRes,
        metricsProgRes,
        expendituresRes,
        disbursementsRes,
        companyUploadsRes,
        ngoUploadsRes
      ] = await Promise.all(queries);

      setFoundationContributions(foundationContributionsBatch1Res.data || []);

      // --- Process Project & NGO Mapping ---
      // Explicitly Type cast here to fix TS errors
      const projectNgosList = (projectNgosRes.data || []) as { project_id: string; ngo_id: string; status: string }[];
      const projectNgoMap = new Map<string, string>();

      projectNgosList.forEach((pn) => {
        if (!projectNgoMap.has(pn.project_id) || pn.status === 'accepted') {
          projectNgoMap.set(pn.project_id, pn.ngo_id);
        }
      });

      const ngoMap = new Map(finalNgos.map((ngo: any) => [ngo.id, ngo]));

      const projectsWithNgo = rawProjects.map((project: any) => {
        const ngoId = projectNgoMap.get(project.id);
        return {
          ...project,
          ngo_id: ngoId || undefined,
          ngo: ngoId ? ngoMap.get(ngoId) : null
        };
      });

      setProjects(projectsWithNgo);

      // --- Process Metrics ---
      const projectToNgoNameMap = new Map<string, string[]>();
      const projectToNgoIdMap = new Map<string, string[]>();
      projectNgosList.forEach((pn) => {
        const ngo = ngoMap.get(pn.ngo_id);
        if (!ngo) return;
        if (!projectToNgoNameMap.has(pn.project_id)) projectToNgoNameMap.set(pn.project_id, []);
        if (!projectToNgoIdMap.has(pn.project_id)) projectToNgoIdMap.set(pn.project_id, []);
        const names = projectToNgoNameMap.get((pn as any).project_id)!;
        const ids = projectToNgoIdMap.get((pn as any).project_id)!;
        if (!names.includes((ngo as any).legal_name)) names.push((ngo as any).legal_name);
        if (!ids.includes((ngo as any).id)) ids.push((ngo as any).id);
      });

      const rawMetricDefs = (metricsDefRes.data || []) as any[];
      const rawMetricProgs = (metricsProgRes.data || []) as any[];

      const metricsList = rawMetricDefs.map((def: any) => {
        // Match progress by metric name and project
        const prog = rawMetricProgs.find(
          (p: any) => p.metric_name === def.metric_name && p.project_id === def.project_id
        );

        const names = projectToNgoNameMap.get(def.project_id) || [];
        const ids = projectToNgoIdMap.get(def.project_id) || [];
        return {
          id: def.id,
          project_id: def.project_id,
          metric_name: def.metric_name,
          target_value: def.target_value,
          current_value: prog ? prog.current_value : 0,
          unit: def.unit || '',
          ngo_name: names.length > 0 ? names.join(', ') : undefined,
          ngo_ids: ids
        };
      });

      setMetrics(metricsList);

      // --- Process Finances ---
      const filteredExpenditures = (expendituresRes.data || []) as { id: string; project_id: string; expenditure_amount: number; description: string }[];
      setProjectExpenditures(filteredExpenditures);

      setFundDisbursements(disbursementsRes.data || []);

      // --- Process Compliance ---
      const companyComplianceData = (companyUploadsRes.data || []) as any[];
      // Total = unique assignments for this company (not NGO)
      const totalRequiredDocs = rawAssignments.filter((a: any) => !a.ngo_id).length;
      const compliantDocsCount = companyComplianceData.filter(u => ['Yes', 'Completed'].includes(u.compliance_status || '')).length;
      setComplianceSnapshot({ pending: Math.max(0, totalRequiredDocs - compliantDocsCount), done: compliantDocsCount });
      setCompanyUploads(companyComplianceData);

      setNgoUploads(ngoUploadsRes.data || []);

    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      fetchData();
    } else {
      setLoading(false);
      setError('Company ID is missing from URL');
    }
  }, [companyId, fetchData]);

  const dashboardStats = useMemo(() => {
    const totalNGOs = ngos.length;
    const validEntities = companyEntities.filter(e => !e.is_escrow);
    const totalCsrMandate = validEntities.length > 0
      ? validEntities.reduce((sum, e) => sum + (Number(e.csr_mandate) || 0), 0)
      : (Number(companyProfile?.csr_mandate) || 0);

    const activeProjectIds = new Set(projects.filter(p => p.status === 'active').map(p => p.id));
    const totalUtilised = fundDisbursements
      .filter(disb => activeProjectIds.has(disb.project_id))
      .reduce((sum, disb) => {
        const amount = typeof disb.amount === 'string'
          ? parseFloat(disb.amount)
          : (disb.amount || 0);
        return sum + amount;
      }, 0) + adminExpenses.reduce((sum, exp) => {
        const amount = typeof exp.amount === 'string'
          ? parseFloat(exp.amount)
          : (exp.amount || 0);
        return sum + amount;
      }, 0);

    const activeProjects = projects.filter(p => p.status === 'active').length;
    const completedProjects = projects.filter(p => p.status === 'completed' || p.status === 'closed').length;

    const totalAdminBudget = validEntities.length > 0
      ? validEntities.reduce((sum, e) => sum + (Number(e.admin_budget) || 0), 0)
      : (Number(companyProfile?.admin_budget) || 0);

    return {
      totalNGOs,
      totalCsrMandate,
      totalUtilised,
      totalAdminBudget,
      activeProjects,
      completedProjects
    };
  }, [projects, ngos, fundDisbursements, adminExpenses, companyProfile, companyEntities, companyCsrMandate]);

  const allNgoComplianceRows = useMemo(() => {
    const data: any[] = [];
    const coveredUploadIds = new Set<string>();

    // 1. Process Assignments (formal requirements)
    companyAssignments
      .filter(a => a.ngo_id)
      .forEach(assignment => {
        const ngo = ngos.find(n => n.id === assignment.ngo_id);
        if (!ngo) return;

        const allDocsLookup = [...ngoDocs, ...companyDocs];
        let doc = allDocsLookup.find(d => d.id === assignment.document_repository_id);

        if (!doc && (!assignment.is_auto)) {
          const uploadsCheck = ngoUploads.filter(u =>
            u.ngo_id === ngo.id &&
            (u.assignment_id === assignment.id || u.document_repository_id === assignment.document_repository_id)
          );
          if (uploadsCheck.length === 0) return;
        }

        let docName = doc?.document_name || 'Unknown Document';
        const dueDate = assignment.due_date || doc?.due_date;

        const uploads = ngoUploads.filter(u =>
          u.ngo_id === ngo.id &&
          (u.assignment_id === assignment.id || u.document_repository_id === assignment.document_repository_id)
        );
        const latest = uploads.length > 0
          ? uploads.sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime())[0]
          : null;

        if (latest) coveredUploadIds.add(latest.id);

        data.push({
          id: assignment.id,
          ngoId: ngo.id,
          ngoName: ngo.legal_name.length > 20 ? ngo.legal_name.substring(0, 20) + '...' : ngo.legal_name,
          documentName: docName,
          status: latest
            ? (['Yes', 'Completed'].includes(latest.compliance_status || '') ? 'Completed' :
              (['No', 'In Progress'].includes(latest.compliance_status || '')) ? 'In Progress' :
                latest.compliance_status === 'Pending' ? 'Pending' : 'In Progress')
            : 'Pending',
          remarks: latest ? (latest.notes || latest.admin_notes || (['No', 'In Progress'].includes(latest.compliance_status || '') ? 'In Progress' : 'Approved & Adopted')) : '-',
          validity: latest?.expiry_date ? `${new Date(latest.expiry_date).toLocaleDateString()}` : (dueDate ? `Due: ${new Date(dueDate).toLocaleDateString()} ` : '-'),
          rawExpiry: latest?.expiry_date || null,
          fileUrl: latest?.file_url,
          submission: latest,
          isOrphan: false
        });
      });

    // 2. Process Orphan NGO Uploads
    const orphans = ngoUploads.filter(u => u.ngo_id && !coveredUploadIds.has(u.id));
    const orphanGroups = new Map<string, any[]>();
    orphans.forEach(u => {
      const key = `${u.ngo_id}::${u.document_repository_id}`;
      if (!orphanGroups.has(key)) orphanGroups.set(key, []);
      orphanGroups.get(key)!.push(u);
    });

    orphanGroups.forEach((uploads, key) => {
      const [ngoId, repoId] = key.split('::');
      const ngo = ngos.find(n => n.id === ngoId);
      if (!ngo) return;

      const latest = uploads.sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime())[0];
      const allDocsLookup = [...ngoDocs, ...companyDocs];
      let doc = allDocsLookup.find(d => d.id === repoId);
      let docName = doc?.document_name || 'Unknown Document';

      data.push({
        id: `orphan-${latest.id}`,
        ngoId: ngo.id,
        ngoName: ngo.legal_name,
        documentName: docName,
        status: ['Yes', 'Completed'].includes(latest.compliance_status || '') ? 'Completed' :
          (['No', 'In Progress'].includes(latest.compliance_status || '')) ? 'In Progress' :
            latest.compliance_status === 'Pending' ? 'Pending' : 'In Progress',
        remarks: latest.notes || latest.admin_notes || 'Manual Upload',
        validity: latest.expiry_date ? `${new Date(latest.expiry_date).toLocaleDateString()}` : '-',
        rawExpiry: latest.expiry_date || null,
        fileUrl: latest.file_url,
        submission: latest,
        isOrphan: true
      });
    });

    return data;
  }, [ngos, companyAssignments, ngoUploads, ngoDocs, companyDocs]);

  const ngoComplianceVisualData = useMemo(() => {
    return ngos.map(ngo => {
      const rows = allNgoComplianceRows.filter(r => r.ngoId === ngo.id);
      const total = rows.length;
      const uploadedCount = rows.filter(r => r.status === 'Completed').length;
      const inProgressCount = rows.filter(r => r.status === 'In Progress').length;
      const pendingCount = Math.max(0, total - uploadedCount - inProgressCount);
      const percent = total > 0 ? Math.min((uploadedCount / total) * 100, 100) : 0;

      let statusColor = '#ef4444';
      if (percent > 30 && percent <= 70) statusColor = '#facc15';
      else if (percent > 70) statusColor = '#10b981';

      return {
        id: ngo.id,
        name: ngo.legal_name,
        logo_url: (() => {
          const raw = (ngo as any).logo_url;
          if (!raw) return null;
          return signedLogoUrls[raw] || raw;
        })(),
        total,
        uploadedCount,
        inProgressCount,
        pendingCount,
        percent,
        statusColor,
        chartData: [
          { name: 'Completed', value: uploadedCount, color: '#10b981' },
          { name: 'In Progress', value: inProgressCount, color: '#facc15' },
          { name: 'Pending', value: Math.max(pendingCount, 0), color: '#cbd5e1' }
        ]
      };
    });
  }, [ngos, allNgoComplianceRows, signedLogoUrls]);

  const ngoDocsTableData = useMemo(() => {
    const filtered = allNgoComplianceRows.filter(r => selectedComplianceNgo === 'all' || r.ngoId === selectedComplianceNgo);
    return filtered.sort((a, b) => {
      const aDaysLeft = a.rawExpiry ? Math.ceil((new Date(a.rawExpiry).getTime() - Date.now()) / (1000 * 3600 * 24)) : 999;
      const bDaysLeft = b.rawExpiry ? Math.ceil((new Date(b.rawExpiry).getTime() - Date.now()) / (1000 * 3600 * 24)) : 999;

      const aIsExpiring = aDaysLeft <= 15;
      const bIsExpiring = bDaysLeft <= 15;

      if (aIsExpiring && !bIsExpiring) return -1;
      if (!aIsExpiring && bIsExpiring) return 1;

      return a.ngoName.localeCompare(b.ngoName);
    });
  }, [allNgoComplianceRows, selectedComplianceNgo]);

  // NGO-wise Budget vs Utilised chart data
  const ngoBudgetVsUtilisedData = useMemo(() => {
    const ngoMap: Record<string, { name: string; budget: number; utilised: number }> = {};
    projects.filter(p => p.status === 'active').forEach(project => {
      if (project.ngo_id) {
        const ngoName = (project.ngo as any)?.legal_name || 'Unknown NGO';
        if (!ngoMap[project.ngo_id]) {
          ngoMap[project.ngo_id] = { name: ngoName, budget: 0, utilised: 0 };
        }
        ngoMap[project.ngo_id].budget += project.budget || 0;
      }
    });

    const projectToNgoMap = new Map<string, string>();
    projects.filter(p => p.status === 'active').forEach(project => {
      if (project.ngo_id) {
        projectToNgoMap.set(project.id, project.ngo_id);
      }
    });

    fundDisbursements.forEach(disb => {
      const ngoId = projectToNgoMap.get(disb.project_id);
      if (ngoId && ngoMap[ngoId]) {
        ngoMap[ngoId].utilised += parseFloat(disb.amount) || 0;
      }
    });

    return Object.values(ngoMap).map(ngo => ({
      name: ngo.name,
      Budget: ngo.budget,
      Utilised: ngo.utilised
    }));
  }, [projects, fundDisbursements]);

  useEffect(() => {
    const fetchSignedLogos = async () => {
      const urlsToSign = new Set<string>();
      if (companyProfile?.logo_url) urlsToSign.add(companyProfile.logo_url);
      if (companyProfile?.dashboard_images && Array.isArray(companyProfile.dashboard_images)) {
        companyProfile.dashboard_images.forEach((url: string) => urlsToSign.add(url));
      }
      companyEntities.forEach(e => {
        if (e.logo_url) urlsToSign.add(e.logo_url);
      });

      const newSignedUrls: Record<string, string> = { ...signedLogoUrls };
      let updated = false;

      // Prepare company-documents paths
      const companyPaths: { url: string, path: string }[] = [];
      for (const url of Array.from(urlsToSign)) {
        if (!url || url === 'null' || url === 'undefined' || newSignedUrls[url]) continue;
        let path = url;
        if (url.includes('/object/public/company-documents/')) path = url.split('/object/public/company-documents/')[1].split('?')[0];
        else if (url.includes('/company-documents/')) path = url.split('/company-documents/')[1].split('?')[0];
        else if (url.startsWith('company-documents/')) path = url.replace('company-documents/', '');
        if (path) companyPaths.push({ url, path: decodeURIComponent(path) });
      }

      // Bulk sign company-documents
      if (companyPaths.length > 0) {
        try {
          const { data, error } = await supabase.storage.from('company-documents').createSignedUrls(companyPaths.map(p => p.path), 3600);
          if (data && !error) {
            data.forEach((item, i) => {
              if (item.signedUrl) {
                newSignedUrls[companyPaths[i].url] = item.signedUrl;
                updated = true;
              }
            });
          }
        } catch (err) { console.error('Failed to bulk sign company logos:', err); }
      }

      // Prepare ngo-documents paths
      const ngoPaths: { url: string, path: string }[] = [];
      const ngoLogoUrls = ngos.map((n: any) => n.logo_url).filter(Boolean);
      for (const url of ngoLogoUrls) {
        if (!url || newSignedUrls[url]) continue;
        let path = url;
        if (url.includes('/object/public/ngo-documents/')) path = url.split('/object/public/ngo-documents/')[1].split('?')[0];
        else if (url.includes('/ngo-documents/')) path = url.split('/ngo-documents/')[1].split('?')[0];
        if (path) ngoPaths.push({ url, path: decodeURIComponent(path) });
      }

      // Bulk sign ngo-documents
      if (ngoPaths.length > 0) {
        try {
          const { data, error } = await supabase.storage.from('ngo-documents').createSignedUrls(ngoPaths.map(p => p.path), 3600);
          if (data && !error) {
            data.forEach((item, i) => {
              if (item.signedUrl) {
                newSignedUrls[ngoPaths[i].url] = item.signedUrl;
                updated = true;
              }
            });
          }
        } catch (err) { console.error('Failed to bulk sign NGO logos:', err); }
      }

      if (updated) {
        setSignedLogoUrls(newSignedUrls);
      }
    };

    if (companyProfile || companyEntities.length > 0 || ngos.length > 0) {
      fetchSignedLogos();
    }
  }, [companyProfile, companyEntities, ngos]);

  useEffect(() => {
    // Retry loading logos if signed URLs change
    if (Object.keys(signedLogoUrls).length > 0) {
      setLogoErrors({});
    }
  }, [signedLogoUrls]);

  // Project-wise Budget vs Utilised chart data
  const projectBudgetVsUtilisedData = useMemo(() => {
    const projectMap: Record<string, { name: string; budget: number; utilised: number }> = {};
    projects.filter(p => p.status === 'active').forEach(project => {
      projectMap[project.id] = {
        name: project.name.length > 25 ? project.name.substring(0, 25) + '...' : project.name,
        budget: project.budget || 0,
        utilised: 0
      };
    });

    fundDisbursements.forEach(disb => {
      if (projectMap[disb.project_id]) {
        projectMap[disb.project_id].utilised += parseFloat(disb.amount as string) || 0;
      }
    });

    return Object.values(projectMap).map(project => ({
      name: project.name,
      Budget: project.budget,
      Utilised: project.utilised
    }));
  }, [projects, fundDisbursements]);

  // Project-wise Impact Metrics (Target vs Achieved)
  const impactMetricsChartData = useMemo(() => {
    let filteredMetrics = metrics;

    // Respect the global NGO filter based on currently visible projects
    const activeProjectIds = new Set(projects.filter(p => p.status === 'active').map(p => p.id));
    filteredMetrics = filteredMetrics.filter(m => activeProjectIds.has(m.project_id));

    if (selectedImpactProject !== 'all') {
      filteredMetrics = filteredMetrics.filter(m => m.project_id === selectedImpactProject);
    }

    return filteredMetrics.map(m => {
      const project = projects.find(p => p.id === m.project_id);
      const projectName = project ? (project.name.length > 15 ? project.name.substring(0, 15) + '...' : project.name) : 'Unknown';
      const metricName = m.metric_name.length > 20 ? m.metric_name.substring(0, 20) + '...' : m.metric_name;

      return {
        id: m.id,
        name: selectedImpactProject === 'all' ? `${projectName} - ${metricName} ` : metricName,
        Target: Number(m.target_value) || 0,
        Achieved: Number(m.current_value) || 0,
        unit: m.unit || '' // Keep unit for tooltip if needed
      };
    });
  }, [projects, metrics, selectedImpactProject]);

  const allCompanyComplianceRows = useMemo(() => {
    const data: any[] = [];
    // Use document_assignments as source of truth — one row per assignment
    companyAssignments
      .filter(a => !a.ngo_id) // Only company/entity assignments (not NGO)
      .forEach(assignment => {
        const allDocsLookup = [...companyDocs, ...ngoDocs];
        let doc = allDocsLookup.find(d => d.id === assignment.document_repository_id);

        // Match Compliance page logic: Ignore ghost assignments pointing to deleted/inactive templates
        if (!doc && (!assignment.is_auto)) {
          const uploadsCheck = companyUploads.filter(u =>
            u.assignment_id === assignment.id ||
            (u.document_repository_id === assignment.document_repository_id &&
              (assignment.entity_id ? u.entity_id === assignment.entity_id : !u.entity_id))
          );
          if (uploadsCheck.length === 0) return; // Skip empty ghost assignments
        }

        let docName = doc?.document_name || 'Unknown Document';

        // Smart Resolution: Match by name if ID lookup failed
        if (docName === 'Unknown Document') {
          const upload = companyUploads.find(u => u.assignment_id === assignment.id);
          if (upload && upload.file_name) {
            const uploadName = upload.file_name.toLowerCase();
            const nameMatch = allDocsLookup.find(d => {
              const templateName = d.document_name.toLowerCase();
              return uploadName.includes(templateName) || templateName.includes(uploadName.split('_')[0]);
            });
            if (nameMatch) {
              doc = nameMatch;
              docName = nameMatch.document_name;
            }
          }
        }
        const dueDate = assignment.due_date || doc?.due_date;

        // Determine group name and filter ID (Foundation or specific entity)
        let groupName = `${companyName} (Foundation)`;
        let isFoundation = true;
        let filterId = 'foundation';
        if (assignment.entity_id) {
          const entity = companyEntities.find(e => e.id === assignment.entity_id);
          groupName = entity?.name || 'Unknown Entity';
          isFoundation = false;
          filterId = assignment.entity_id;
        }

        // Find matching upload
        const upload = companyUploads.find(u =>
          u.assignment_id === assignment.id ||
          (u.document_repository_id === assignment.document_repository_id &&
            (assignment.entity_id ? u.entity_id === assignment.entity_id : !u.entity_id))
        );

        const row = {
          id: assignment.id,
          filterId: filterId,
          isFoundation: isFoundation,
          documentName: docName,
          companyName: groupName,
          status: upload
            ? (['Yes', 'Completed'].includes(upload.compliance_status || '') ? 'Completed' :
              (['No', 'In Progress'].includes(upload.compliance_status || '')) ? 'In Progress' :
                upload.compliance_status === 'Pending' ? 'Pending' : 'In Progress')
            : 'Pending',
          remarks: upload ? (upload.notes || upload.admin_notes || (['No', 'In Progress'].includes(upload.compliance_status || '') ? 'In Progress' : 'Approved & Adopted')) : '-',
          validity: upload?.expiry_date ? `${new Date(upload.expiry_date).toLocaleDateString()}` : (dueDate ? `Due: ${new Date(dueDate).toLocaleDateString()}` : '-'),
          rawExpiry: upload?.expiry_date || null,
          fileUrl: (upload && upload.file_url !== 'manual') ? upload.file_url : null
        };

        data.push(row);
      });
    return data;
  }, [companyDocs, ngoDocs, companyUploads, companyEntities, companyName, companyAssignments]);

  const companyComplianceVisualData = useMemo(() => {
    const getLogoUrl = (url: string | null | undefined) => {
      if (!url || url === 'null' || url === 'undefined') return null;
      if (signedLogoUrls[url]) return signedLogoUrls[url];
      return url;
    };

    const entityEntries: any[] = [];

    companyEntities.filter(e => !e.is_escrow).forEach(entity => {
      const rows = allCompanyComplianceRows.filter(r => r.filterId === entity.id);
      const total = rows.length;
      const uploadedCount = rows.filter(r => r.status === 'Completed').length;
      const inProgressCount = rows.filter(r => r.status === 'In Progress').length;
      const pendingCount = Math.max(0, total - uploadedCount - inProgressCount);
      const percent = total > 0 ? Math.min((uploadedCount / total) * 100, 100) : 0;

      const statusColor = total > 0
        ? (percent > 70 ? '#10b981' : (percent > 30 ? '#facc15' : '#ef4444'))
        : '#94a3b8';

      entityEntries.push({
        id: entity.id,
        name: entity.name,
        logo_url: getLogoUrl(entity.logo_url),
        total: total,
        uploadedCount,
        inProgressCount,
        pendingCount,
        percent,
        statusColor,
        chartData: [
          { name: 'Completed', value: uploadedCount, color: '#10b981' },
          { name: 'In Progress', value: inProgressCount, color: '#facc15' },
          { name: 'Pending', value: pendingCount, color: '#cbd5e1' }
        ]
      });
    });

    return { entities: entityEntries };
  }, [allCompanyComplianceRows, companyEntities, signedLogoUrls]);

  const entityMandateVisualData = useMemo(() => {
    // Ensure logo URLs are absolute and signed
    const getLogoUrl = (url: string | null | undefined) => {
      if (!url || url === 'null' || url === 'undefined') return null;
      if (signedLogoUrls[url]) return signedLogoUrls[url]; // Use signed URL if available
      return url;
    };

    // Calculate spent per entity by mapping projects -> entities -> expenditures
    // Note: Since we don't have direct project -> entity mapping in `projects` table (it just uses company_id),
    // and `project_expenditures` only link to projects.
    // Given the data model from user description, we assume if `csr_mandate` is at entity level, we might not have perfect expenditure mapping.
    // If not possible, we will just show the Mandate value as target and 0 as spent, unless there's an entity_id on projects.
    // Let's check `projects` mapping: they only have `company_id`.
    // Wait, the user asked to show "Entity wise" - let's split the total Mandate by entity, and if we can't split spent perfectly, we just show Mandate vs what we have.
    // Since we don't know spent per entity, I will just show their CSR Mandate in standard PieCharts with 0% utilized for now unless `entity_id` exists on projects.

    const entries: any[] = [];

    // 1. Foundation / Primary Account
    const fMandate = companyProfile?.setup_type === 'single'
      ? ((Number(companyProfile?.csr_mandate) || 0) - (Number(companyProfile?.admin_budget) || 0))
      : companyEntities.reduce((sum, e) => sum + (Number(e.primary_account_budget) || 0), 0);
    const fSpent = fundDisbursements
      .filter(d => !d.entity_id)
      .reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

    const fPercent = fMandate > 0 ? (fSpent / fMandate) * 100 : (fSpent > 0 ? 100 : 0);
    const fStatusColor = fMandate > 0 ? '#10b981' : '#94a3b8';

    if (companyProfile?.setup_type !== 'single' || companyEntities.filter(e => !e.is_escrow).length === 0) {
      entries.push({
        id: 'foundation_direct',
        name: companyProfile?.name || (companyProfile?.setup_type === 'single' ? 'Primary' : 'Foundation'),
        logo_url: getLogoUrl(companyProfile?.logo_url),
        mandate: fMandate,
        utilised: fSpent,
        percent: fPercent,
        statusColor: fStatusColor,
        chartData: [
          { name: 'Utilised', value: fSpent, color: fStatusColor },
          { name: 'Remaining', value: Math.max(0.1, fMandate - fSpent), color: fMandate > 0 ? '#e5e7eb' : '#cbd5e1' }
        ]
      });
    }

    // 2. Sub Entities
    companyEntities.filter(e => !e.is_escrow).forEach(entity => {
      const eMandate = Number(entity.entity_budget) || 0;
      const eSpent = fundDisbursements
        .filter(d => d.entity_id === entity.id)
        .reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

      const ePercent = eMandate > 0 ? (eSpent / eMandate) * 100 : 0;
      const eStatusColor = eMandate > 0 ? '#10b981' : '#94a3b8'; // Grey for zero mandate

      entries.push({
        id: entity.id,
        name: entity.name,
        logo_url: getLogoUrl(entity.logo_url),
        mandate: eMandate,
        utilised: eSpent,
        percent: ePercent,
        statusColor: eStatusColor,
        chartData: [
          { name: 'Utilised', value: eSpent, color: eStatusColor },
          { name: 'Remaining', value: Math.max(0.1, eMandate - eSpent), color: eMandate > 0 ? '#e5e7eb' : '#cbd5e1' }
        ]
      });
    });

    return entries;
  }, [companyId, companyName, companyProfile, companyEntities, fundDisbursements, signedLogoUrls]);


  const adminExpenseVisualData = useMemo(() => {
    const getLogoUrl = (url: string | null | undefined) => {
      if (!url || url === 'null' || url === 'undefined') return null;
      if (signedLogoUrls[url]) return signedLogoUrls[url];
      return url;
    };

    const entries: any[] = [];

    // 1. Main Company Admin Expenses
    const primaryAdminBudget = Number(companyProfile?.admin_budget) || 0;
    const primaryAdminSpent = adminExpenses
      .filter(e => !e.entity_id)
      .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

    const pPercent = primaryAdminBudget > 0 ? (primaryAdminSpent / primaryAdminBudget) * 100 : (primaryAdminSpent > 0 ? 100 : 0);
    const pStatusColor = primaryAdminBudget > 0 ? '#10b981' : '#94a3b8';

    const pName = companyName || 'Main Company / Admin';
    const isFoundation = pName.toLowerCase().includes('foundation') || pName.toLowerCase().includes('primary account');

    if (!isFoundation) {
      if (companyProfile?.setup_type !== 'single' || companyEntities.filter(e => !e.is_escrow).length === 0) {
        entries.push({
          id: `admin_main`,
          name: pName,
          logo_url: getLogoUrl(companyProfile?.logo_url),
          budget: primaryAdminBudget,
          utilised: primaryAdminSpent,
          percent: pPercent,
          statusColor: pStatusColor,
          chartData: [
            { name: 'Utilised', value: primaryAdminSpent, color: pStatusColor },
            { name: 'Remaining', value: Math.max(0.1, primaryAdminBudget - primaryAdminSpent), color: primaryAdminBudget > 0 ? '#e5e7eb' : '#cbd5e1' }
          ]
        });
      }
    }

    // 2. Sub Entities Admin Expenses
    companyEntities.filter(e => !e.is_escrow).forEach(entity => {
      // Skip foundation/primary account in admin expenses per user request
      const lowerName = (entity.name || '').toLowerCase();
      if (lowerName.includes('foundation') || lowerName.includes('primary account')) return;

      const eAdminBudget = Number(entity.admin_budget) || 0;
      const eAdminSpent = adminExpenses
        .filter(e => e.entity_id === entity.id)
        .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

      const ePercent = eAdminBudget > 0 ? (eAdminSpent / eAdminBudget) * 100 : (eAdminSpent > 0 ? 100 : 0);
      const eStatusColor = eAdminBudget > 0 ? '#10b981' : '#94a3b8'; // Grey for zero budget

      entries.push({
        id: `admin_${entity.id} `,
        name: entity.name,
        logo_url: getLogoUrl(entity.logo_url),
        budget: eAdminBudget,
        utilised: eAdminSpent,
        percent: ePercent,
        statusColor: eStatusColor,
        chartData: [
          { name: 'Utilised', value: eAdminSpent, color: eStatusColor },
          { name: 'Remaining', value: Math.max(0.1, eAdminBudget - eAdminSpent), color: eAdminBudget > 0 ? '#e5e7eb' : '#cbd5e1' }
        ]
      });
    });

    return entries;
  }, [companyName, companyProfile, companyEntities, adminExpenses, signedLogoUrls]);

  const foundationUtilisationVisualData = useMemo(() => {
    const getLogoUrl = (url: string | null | undefined) => {
      if (!url || url === 'null' || url === 'undefined') return null;
      if (signedLogoUrls[url]) return signedLogoUrls[url];
      return url;
    };

    const entries: any[] = [];

    companyEntities.filter(e => !e.is_escrow).forEach(entity => {
      const ePrimaryBudget = Number(entity.primary_account_budget) || 0;
      const eActualSent = foundationContributions
        .filter(c => c.entity_id === entity.id)
        .reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);

      const ePercent = ePrimaryBudget > 0 ? (eActualSent / ePrimaryBudget) * 100 : (eActualSent > 0 ? 100 : 0);
      const eStatusColor = ePrimaryBudget > 0
        ? (ePercent > 70 ? '#10b981' : (ePercent > 30 ? '#facc15' : '#ef4444'))
        : '#94a3b8';

      entries.push({
        id: `foundation_${entity.id} `,
        name: entity.name,
        logo_url: getLogoUrl(entity.logo_url),
        budget: ePrimaryBudget,
        utilised: eActualSent,
        percent: ePercent,
        statusColor: eStatusColor,
        chartData: [
          { name: 'Sent to ADMF', value: eActualSent, color: eStatusColor },
          { name: 'Remaining', value: Math.max(0.1, ePrimaryBudget - eActualSent), color: ePrimaryBudget > 0 ? '#e5e7eb' : '#cbd5e1' }
        ]
      });
    });

    return entries;
  }, [signedLogoUrls, companyEntities, foundationContributions]);

  const escrowVisualData = useMemo(() => {
    const getLogoUrl = (url: string | null | undefined) => {
      if (!url || url === 'null' || url === 'undefined') return null;
      if (signedLogoUrls[url]) return signedLogoUrls[url];
      return url;
    };

    const entries: any[] = [];

    companyEntities.filter(e => e.is_escrow).forEach(entity => {
      // For Escrow Accounts, we assume their entity_budget is the mandate
      const eMandate = Number(entity.csr_mandate) || 0;
      const eSpent = fundDisbursements
        .filter(d => d.entity_id === entity.id)
        .reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

      const ePercent = eMandate > 0 ? (eSpent / eMandate) * 100 : 0;
      const eStatusColor = eMandate > 0 ? '#10b981' : '#94a3b8';

      entries.push({
        id: entity.id,
        name: entity.name,
        logo_url: getLogoUrl(entity.logo_url),
        mandate: eMandate,
        utilised: eSpent,
        percent: ePercent,
        statusColor: eStatusColor,
        chartData: [
          { name: 'Utilised', value: eSpent, color: eStatusColor },
          { name: 'Remaining', value: Math.max(0.1, eMandate - eSpent), color: eMandate > 0 ? '#e5e7eb' : '#cbd5e1' }
        ]
      });
    });

    return entries;
  }, [companyEntities, fundDisbursements, signedLogoUrls]);

  const companyDocsTableData = useMemo(() => {
    const sortFn = (a: any, b: any) => {
      const aDaysLeft = a.rawExpiry ? Math.ceil((new Date(a.rawExpiry).getTime() - Date.now()) / (1000 * 3600 * 24)) : 999;
      const bDaysLeft = b.rawExpiry ? Math.ceil((new Date(b.rawExpiry).getTime() - Date.now()) / (1000 * 3600 * 24)) : 999;

      const aIsExpiring = aDaysLeft <= 15;
      const bIsExpiring = bDaysLeft <= 15;

      if (aIsExpiring && !bIsExpiring) return -1;
      if (!aIsExpiring && bIsExpiring) return 1;

      return 0; // Maintain order
    };

    const filtered = allCompanyComplianceRows.filter(r => selectedComplianceCompany === 'all' || r.filterId === selectedComplianceCompany);

    return {
      foundationDocs: filtered.filter(r => r.isFoundation).sort(sortFn),
      entityDocs: filtered.filter(r => !r.isFoundation).sort(sortFn)
    };
  }, [allCompanyComplianceRows, selectedComplianceCompany]);

  const foundationComplianceVisualData = useMemo(() => {
    const rows = allCompanyComplianceRows.filter(r => r.isFoundation);

    const total = rows.length;
    const uploadedCount = rows.filter(r => r.status === 'Completed').length;
    const inProgressCount = rows.filter(r => r.status === 'In Progress').length;
    const pendingCount = Math.max(0, total - uploadedCount - inProgressCount);
    const percent = total > 0 ? (uploadedCount / total) * 100 : 0;

    const statusColor = percent === 100 ? '#10b981' : (percent >= 50 ? '#facc15' : '#ef4444');

    return {
      id: 'foundation',
      name: companyName || 'Foundation',
      logo_url: companyProfile?.logo_url,
      total,
      uploadedCount,
      inProgressCount,
      pendingCount,
      percent,
      statusColor,
      chartData: [
        { name: 'Completed', value: uploadedCount, color: '#10b981' },
        { name: 'In Progress', value: inProgressCount, color: '#facc15' },
        { name: 'Pending', value: pendingCount, color: '#cbd5e1' }
      ]
    };
  }, [allCompanyComplianceRows, companyName, companyProfile]);


  const formatCurrency = (amount: number) => {
    return (amount || 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    });
  };

  const formatShortINR = (value: number) => {
    if (!value || value === 0) return '₹0';
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(value % 100000 === 0 ? 0 : 1)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(0)}k`;
    return `₹${value}`;
  };

  const CustomYAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const text = payload.value || '';
    const words = text.split(' ');

    let line1 = '';
    let line2 = '';

    for (let i = 0; i < words.length; i++) {
      if ((line1 + ' ' + words[i]).trim().length <= 18 || i === 0) {
        line1 = (line1 + ' ' + words[i]).trim();
      } else {
        line2 = words.slice(i).join(' ');
        break;
      }
    }

    if (line2.length > 18) {
      line2 = line2.substring(0, 16) + '...';
    }

    return (
      <g transform={`translate(${x},${y})`}>
        {line2 ? (
          <>
            <text x={-6} y={-4} textAnchor="end" fill="#334155" fontSize={10} fontWeight={500}>
              {line1}
            </text>
            <text x={-6} y={8} textAnchor="end" fill="#334155" fontSize={10} fontWeight={500}>
              {line2}
            </text>
          </>
        ) : (
          <text x={-6} y={3} textAnchor="end" fill="#334155" fontSize={10} fontWeight={500}>
            {line1}
          </text>
        )}
      </g>
    );
  };



  const handleDownloadPDF = async () => {
    if (!dashboardRef.current) return;
    setIsExporting(true);

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pdfWidth - (2 * margin);

      const container = dashboardRef.current;
      const sections = container.querySelectorAll('.pdf-section');
      const headerElement = container.querySelector('.pdf-header') as HTMLElement;

      // Capture Header once
      let headerImgData = '';
      let headerHeight = 0;
      if (headerElement) {
        const headerCanvas = await html2canvas(headerElement, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#f5f1f0',
          windowWidth: 1400
        });
        headerImgData = headerCanvas.toDataURL('image/png');
        headerHeight = (headerCanvas.height * contentWidth) / headerCanvas.width;
      }

      const addHeaderAndFooter = (pagePdf: jsPDF, pageNum: number) => {
        // Header Logos
        if (headerImgData) {
          pagePdf.addImage(headerImgData, 'PNG', margin, 5, contentWidth, headerHeight);
        }

        // Timestamp
        pagePdf.setFontSize(8);
        pagePdf.setTextColor(150);
        pagePdf.text(`Generated on: ${new Date().toLocaleString()} `, margin, headerHeight + 10);

        // Footer
        pagePdf.setFontSize(8);
        pagePdf.setTextColor(150);
        pagePdf.text("Anchored and Developed By TBL Consultancy Services", pdfWidth - 85, pdfHeight - 10);
        pagePdf.text(`Page ${pageNum}`, margin, pdfHeight - 10);
      };

      let currentY = headerHeight + 20; // Start below header
      let currentPage = 1;
      addHeaderAndFooter(pdf, currentPage);

      for (let i = 0; i < sections.length; i++) {
        const section = sections[i] as HTMLElement;

        // Clone and hide buttons in the section
        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#f5f1f0',
          windowWidth: 1400,
          onclone: (clonedDoc) => {
            const btn = clonedDoc.querySelector('.btn-outline');
            if (btn) (btn as HTMLElement).style.display = 'none';
          }
        });

        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        const imgHeight = (imgProps.height * contentWidth) / imgProps.width;

        // Check if section fits on current page
        if (currentY + imgHeight > pdfHeight - 15) {
          pdf.addPage();
          currentPage++;
          currentY = 25;
          addHeaderAndFooter(pdf, currentPage);
        }

        pdf.addImage(imgData, 'PNG', margin, currentY, contentWidth, imgHeight);
        currentY += imgHeight + 5; // spacing between sections
      }

      pdf.save(`${companyProfile?.name || 'Company'}_Dashboard_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;
  if (error) return <div style={{ padding: '2rem' }}><div className="toast toast-error">{error}</div></div>;
  if (!companyId) return <div style={{ padding: '2rem' }}><div className="toast toast-error">Invalid company ID.</div></div>;

  return (
    <>
      <style>{`
        .dashboard-scorecards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }
        .dashboard-scorecard {
          background: white; border-radius: 12px; padding: 1.5rem;
          box-shadow: var(--shadow); border: 1px solid var(--border-light);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .dashboard-scorecard:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
        .scorecard-value { font-size: 1.75rem; font-weight: 700; margin-bottom: 0.25rem; }
        .scorecard-label { font-size: 0.875rem; color: var(--text-secondary); font-weight: 500; }
        .filter-section { display: flex; gap: 1rem; margin-bottom: 2rem; flex-wrap: wrap; }
        .filter-group { display: flex; flex-direction: column; gap: 0.5rem; }
        .filter-group label { font-size: 0.875rem; font-weight: 600; color: var(--text); }
        .filter-group select { padding: 0.5rem 1rem; border: 1px solid var(--border-light); border-radius: 8px; font-size: 0.875rem; background: white; cursor: pointer; }
        
        .pdf-header { display: none; }
        @media print { .pdf-header { display: flex !important; } }

        /* Custom Scrollbar for Vertical Scrolling */
        .project-scroll-container::-webkit-scrollbar { width: 6px; }
        .project-scroll-container::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 3px; }
        .project-scroll-container::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        .project-scroll-container::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div id="dashboard-container" ref={dashboardRef} style={{ padding: '1.25rem 2rem 6rem 2rem', maxWidth: '1400px', margin: '0 auto' }}>
        {/* PDF-only Header (Off-screen but rendered for pre-loading) */}
        <div className="pdf-header" style={{
          position: 'absolute',
          top: '-10000px',
          left: '0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 0',
          marginBottom: '30px',
          borderBottom: '2px solid #e2e8f0',
          width: '100%',
          backgroundColor: '#f5f1f0'
        }}>
          <div style={{ width: '250px', display: 'flex', justifyContent: 'flex-start' }}>
            <img
              src={signedLogoUrls[companyProfile?.logo_url] || companyProfile?.logo_url}
              alt="Company Logo"
              style={{ height: '60px', objectFit: 'contain' }}
              onError={(e: any) => e.target.style.display = 'none'}
            />
          </div>
          <div style={{ width: '250px', display: 'flex', justifyContent: 'flex-end' }}>
            <img src={tblLogo} alt="TBL Logo" style={{ height: '60px', objectFit: 'contain' }} />
          </div>
        </div>

        {/* Welcome Section - Center Aligned */}
        <div className="pdf-section" style={{
          position: 'relative',
          textAlign: 'center',
          marginBottom: '2rem',
          minHeight: '210px',
          height: 'auto',
          padding: '2.25rem 1.5rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '20px',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.15)',
          boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)'
        }}>
          {/* Export Button */}
          <button
            onClick={handleDownloadPDF}
            disabled={isExporting}
            className="btn btn-outline"
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              zIndex: 10,
              backgroundColor: isExporting ? 'rgba(241, 245, 249, 0.9)' : 'rgba(255,255,255,0.9)',
              color: isExporting ? '#64748b' : '#0f172a',
              borderColor: '#e2e8f0',
              padding: '0.4rem 0.8rem',
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              cursor: isExporting ? 'not-allowed' : 'pointer'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
              <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
            </svg>
            {isExporting ? 'Preparing PDF...' : 'Download PDF'}
          </button>

          {/* Scrolling Background Wrapper */}
          <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: '100%', overflow: 'hidden', borderRadius: '20px' }}>
            <div className="scrolling-bg-container" style={{ animation: 'scrollHorizontalReverse 60s linear infinite' }}>
              {companyProfile?.dashboard_images?.length > 0 ? (
                // If custom images exist, repeat them a few times to create an infinite scroll effect
                [...companyProfile.dashboard_images, ...companyProfile.dashboard_images, ...companyProfile.dashboard_images, ...companyProfile.dashboard_images].map((url: string, i: number) => (
                  <img
                    key={i}
                    src={signedLogoUrls[url] || url}
                    className="scrolling-bg-image"
                    alt=""
                    style={{ height: '100%', width: '450px', objectFit: 'cover', objectPosition: 'center center' }}
                  />
                ))
              ) : (
                // Fallback to static images if no custom images
                [1, 2, 3, 4, 1, 2, 3, 4].map((num, i) => (
                  <img
                    key={i}
                    src={`/images/dashboard/${num}.jpg`}
                    className="scrolling-bg-image"
                    alt=""
                    style={{ height: '100%', width: '450px', objectFit: 'cover', objectPosition: 'center center' }}
                  />
                ))
              )}
            </div>
          </div>

          {/* Contrast Overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.5))',
            zIndex: 1
          }} />

          <div style={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            {/* Company Logo in Welcome Banner */}
            {(signedLogoUrls[companyProfile?.logo_url] || companyProfile?.logo_url) && !logoErrors[companyProfile?.id || 'main'] && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.95)',
                padding: '8px 18px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <img
                  src={signedLogoUrls[companyProfile?.logo_url] || companyProfile?.logo_url}
                  alt={`${companyName} logo`}
                  style={{
                    maxHeight: '45px',
                    maxWidth: '160px',
                    objectFit: 'contain'
                  }}
                  onError={() => setLogoErrors(prev => ({ ...prev, [companyProfile?.id || 'main']: true }))}
                />
              </div>
            )}

            <h1 style={{
              fontSize: 'clamp(1.35rem, 3.2vw, 2.2rem)',
              fontWeight: 800,
              color: '#fff',
              margin: 0,
              textShadow: '0 2px 8px rgba(0,0,0,0.6)',
              letterSpacing: '-0.5px',
              lineHeight: 1.25
            }}>
              {companyName ? `Welcome to ${companyName}` : 'Company Dashboard'}
            </h1>
          </div>
        </div>

        {/* Scorecards */}
        <div className="dashboard-scorecards pdf-section">
          <ScoreCard label="Total NGOs" value={dashboardStats.totalNGOs} color="#10b981" />
          <ScoreCard label="Total CSR Mandate" value={formatCurrency(dashboardStats.totalCsrMandate)} color="#10b981" />
          <ScoreCard label="Total Utilised till Date" value={formatCurrency(dashboardStats.totalUtilised)} color="#10b981" />
          <ScoreCard label="Active Projects" value={dashboardStats.activeProjects} color="#10b981" />
        </div>

        {/* Entity CSR Mandates */}
        {(companyEntities.filter(e => !e.is_escrow).length > 0 || companyProfile?.setup_type === 'single') && (
          <div className="card pdf-section" style={{ marginBottom: '2rem', padding: '1.5rem', background: '#fdfdfd' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1.5rem' }}>
              CSR Mandate
            </h3>
            <div style={{ display: 'flex', justifyContent: 'space-evenly', flexWrap: 'wrap', gap: '2rem', alignItems: 'flex-end', width: '100%' }}>
              {companyEntities.filter(e => !e.is_escrow).length > 0 ? companyEntities.filter(e => !e.is_escrow).map(entity => {
                const logoUrl = signedLogoUrls[entity.logo_url] || entity.logo_url;
                return (
                  <div key={entity.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '220px' }}>
                    <div style={{ height: '40px', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {logoUrl && !logoErrors[entity.id] ? (
                        <img
                          src={logoUrl}
                          alt={`${entity.name} logo`}
                          style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                          onError={() => setLogoErrors(prev => ({ ...prev, [entity.id]: true }))}
                        />
                      ) : (
                        <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text)', textAlign: 'center' }}>
                          {entity.name}
                        </div>
                      )}
                    </div>
                    <div style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'white',
                      border: '1px solid var(--border-light)',
                      borderRadius: '4px',
                      textAlign: 'center',
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--text)',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                      {(Number(entity.csr_mandate) || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 })}
                    </div>
                  </div>
                );
              }) : (
                companyProfile?.setup_type === 'single' && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '220px' }}>
                    <div style={{ height: '40px', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {(signedLogoUrls[companyProfile?.logo_url] || companyProfile?.logo_url) && !logoErrors[companyProfile?.id || 'main'] ? (
                        <img
                          src={signedLogoUrls[companyProfile?.logo_url] || companyProfile?.logo_url}
                          alt={`${companyName} logo`}
                          style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                          onError={() => setLogoErrors(prev => ({ ...prev, [companyProfile?.id || 'main']: true }))}
                        />
                      ) : (
                        <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text)', textAlign: 'center' }}>
                          {companyName}
                        </div>
                      )}
                    </div>
                    <div style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'white',
                      border: '1px solid var(--border-light)',
                      borderRadius: '4px',
                      textAlign: 'center',
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--text)',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                      {(Number(companyProfile?.csr_mandate) || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 })}
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Total Split Row */}
            <div style={{
              marginTop: '2rem',
              paddingTop: '1.5rem',
              borderTop: '1px dashed var(--border-light)',
              display: 'flex',
              justifyContent: 'center',
              gap: '4rem',
              flexWrap: 'wrap'
            }}>
              {/* Project Split (95%) */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '280px' }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.75rem', textAlign: 'center' }}>
                  Amount to be spent on projects (95%)
                </div>
                <div style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  textAlign: 'center',
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  color: '#10b981',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}>
                  {(dashboardStats.totalCsrMandate * 0.95).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 })}
                </div>
              </div>

              {/* Admin Split (5%) */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '280px' }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.75rem', textAlign: 'center' }}>
                  Administrative Overhead (5%)
                </div>
                <div style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  textAlign: 'center',
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  color: '#0f766e',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}>
                  {(dashboardStats.totalCsrMandate * 0.05).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Combined Mandate, Escrow & Admin Sections (SINGLE COMPANY - NEW UX) */}
        {companyProfile?.setup_type === 'single' && (
          <div className="card pdf-section" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', alignItems: 'flex-start' }}>

            {/* Column 1: Escrow Account Utilisation Section */}
            {escrowVisualData.length > 0 && (
              <div style={{ flex: '1 1 250px' }}>
                <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Escrow Account Utilisation
                  <span style={{ fontSize: '0.7rem', padding: '0.1rem 0.3rem', backgroundColor: '#fce7f3', color: '#be185d', borderRadius: '4px', marginLeft: '0.5rem', textTransform: 'none', letterSpacing: 'normal', fontWeight: 600 }}>Escrow</span>
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
                  {escrowVisualData.map(entityData => (
                    <div key={entityData.id} style={{ textAlign: 'center', flex: '1', minWidth: '160px', maxWidth: '260px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {/* Name Only */}
                      <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', wordWrap: 'break-word', textAlign: 'center', maxWidth: '180px' }}>
                          {entityData.name}
                        </div>
                      </div>

                      {/* Gauge */}
                      <div style={{ position: 'relative', height: '110px', width: '210px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={entityData.chartData}
                              cx="50%"
                              cy="100%"
                              startAngle={180}
                              endAngle={0}
                              innerRadius={75}
                              outerRadius={100}
                              paddingAngle={0}
                              cornerRadius={10}
                              dataKey="value"
                              stroke="none"
                            >
                              {entityData.chartData.map((entry: any, index: number) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ position: 'absolute', bottom: '0', left: '50%', transform: 'translateX(-50%)', textAlign: 'center', width: '100%' }}>
                          <span style={{ fontSize: '1.6rem', fontWeight: 700, color: entityData.statusColor || '#10b981' }}>
                            {entityData.mandate === 0 ? '-' : `${entityData.percent.toFixed(1)}%`}
                          </span>
                        </div>
                      </div>

                      {/* Values below gauge */}
                      <div style={{ marginTop: '0.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: entityData.statusColor || '#10b981' }}>{formatCurrency(entityData.utilised)}</div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-secondary)' }}>of {formatCurrency(entityData.mandate)} utilised</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Column 2: Project Utilisation */}
            <div style={{ flex: '1 1 250px' }}>
              <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem', textAlign: 'center' }}>
                {companyProfile?.setup_type === 'single' ? 'CSR Mandate' : 'Direct Utilisation'}
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
                {entityMandateVisualData.length > 0 ? entityMandateVisualData.map(entityData => (
                  <div key={entityData.id} style={{ textAlign: 'center', flex: '1', minWidth: '160px', maxWidth: '260px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {/* Name Only */}
                    <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', wordWrap: 'break-word', textAlign: 'center', maxWidth: '180px' }}>
                        {entityData.name}
                      </div>
                    </div>

                    {/* Gauge */}
                    <div style={{ position: 'relative', height: '110px', width: '210px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={entityData.chartData}
                            cx="50%"
                            cy="100%"
                            startAngle={180}
                            endAngle={0}
                            innerRadius={75}
                            outerRadius={100}
                            paddingAngle={0}
                            cornerRadius={10}
                            dataKey="value"
                            stroke="none"
                          >
                            {entityData.chartData.map((entry: any, index: number) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ position: 'absolute', bottom: '0', left: '50%', transform: 'translateX(-50%)', textAlign: 'center', width: '100%' }}>
                        <span style={{ fontSize: '1.6rem', fontWeight: 700, color: entityData.statusColor || '#10b981' }}>
                          {entityData.mandate === 0 ? '-' : `${entityData.percent.toFixed(1)}%`}
                        </span>
                      </div>
                    </div>

                    {/* Values below gauge */}
                    <div style={{ marginTop: '0.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: entityData.statusColor || '#10b981' }}>{formatCurrency(entityData.utilised)}</div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-secondary)' }}>of {formatCurrency(entityData.mandate)} utilised</div>
                    </div>
                  </div>
                )) : (
                  <div style={{ width: '100%', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    No CSR Mandate entries found.
                  </div>
                )}
              </div>
            </div>

            {/* Column 3: Admin Expenses Section */}
            <div style={{ flex: '1 1 250px' }}>
              <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <span>Admin Expenses</span>
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
                {adminExpenseVisualData.length > 0 ? adminExpenseVisualData.map(entityData => (
                  <div key={entityData.id} style={{ textAlign: 'center', flex: '1', minWidth: '160px', maxWidth: '260px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {/* Name Only */}
                    <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', wordWrap: 'break-word', textAlign: 'center', maxWidth: '180px' }}>
                        {entityData.name}
                      </div>
                    </div>

                    {/* Gauge */}
                    <div style={{ position: 'relative', height: '110px', width: '210px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={entityData.chartData}
                            cx="50%"
                            cy="100%"
                            startAngle={180}
                            endAngle={0}
                            innerRadius={75}
                            outerRadius={100}
                            paddingAngle={0}
                            cornerRadius={10}
                            dataKey="value"
                            stroke="none"
                          >
                            {entityData.chartData.map((entry: any, index: number) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ position: 'absolute', bottom: '0', left: '50%', transform: 'translateX(-50%)', textAlign: 'center', width: '100%' }}>
                        <span style={{ fontSize: '1.6rem', fontWeight: 700, color: '#10b981' }}>
                          {entityData.budget === 0 ? '-' : `${entityData.percent.toFixed(1)}%`}
                        </span>
                      </div>
                    </div>

                    {/* Values below gauge */}
                    <div style={{ marginTop: '0.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: entityData.statusColor || '#10b981' }}>{formatCurrency(entityData.utilised)}</div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-secondary)' }}>of {formatCurrency(entityData.budget)} utilised</div>
                    </div>
                  </div>
                )) : (
                  <div style={{ width: '100%', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    No Admin Budgets configured.
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
        )}

        {/* Foundation Split Sections */}
        {companyProfile?.setup_type !== 'single' && (
          <>
            {/* Direct Utilisation Card */}
            <div className="card pdf-section" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2rem', borderBottom: '1px solid var(--border-light)', paddingBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>
                  CSR Mandate vs Utilised Till Date - Direct Utilisation
                </h3>
                <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Total: {formatCurrency(dashboardStats.totalUtilised)} / {formatCurrency(dashboardStats.totalCsrMandate)}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
                {entityMandateVisualData.filter(e => e.id !== 'foundation_direct').length > 0 ? entityMandateVisualData.filter(e => e.id !== 'foundation_direct').map(entityData => (
                  <div key={entityData.id} style={{ textAlign: 'center', flex: '1', minWidth: '160px', maxWidth: '260px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', wordWrap: 'break-word', textAlign: 'center', maxWidth: '180px' }}>
                        {entityData.name}
                      </div>
                    </div>
                    <div style={{ position: 'relative', height: '110px', width: '210px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={entityData.chartData} cx="50%" cy="100%" startAngle={180} endAngle={0} innerRadius={75} outerRadius={100} paddingAngle={0} cornerRadius={10} dataKey="value" stroke="none">
                            {entityData.chartData.map((entry: any, index: number) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ position: 'absolute', bottom: '0', left: '50%', transform: 'translateX(-50%)', textAlign: 'center', width: '100%' }}>
                        <span style={{ fontSize: '1.6rem', fontWeight: 700, color: entityData.statusColor || '#10b981' }}>
                          {entityData.mandate === 0 ? '-' : `${entityData.percent.toFixed(1)}%`}
                        </span>
                      </div>
                    </div>
                    <div style={{ marginTop: '0.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 600, color: entityData.statusColor || '#10b981' }}>{formatCurrency(entityData.utilised)}</div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>of {formatCurrency(entityData.mandate)} utilised</div>
                    </div>
                  </div>
                )) : (
                  <div style={{ width: '100%', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    No CSR Mandate entries found.
                  </div>
                )}
              </div>
            </div>

            {/* Escrow Account Utilisation Card */}
            {escrowVisualData.length > 0 && (
              <div className="card pdf-section" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2rem', borderBottom: '1px solid var(--border-light)', paddingBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center' }}>
                    Escrow Account Utilisation
                    <span style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', backgroundColor: '#fce7f3', color: '#be185d', borderRadius: '4px', marginLeft: '0.75rem', textTransform: 'none', letterSpacing: 'normal', fontWeight: 600 }}>Escrow</span>
                  </h3>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
                  {escrowVisualData.map(entityData => (
                    <div key={entityData.id} style={{ textAlign: 'center', flex: '1', minWidth: '160px', maxWidth: '260px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', wordWrap: 'break-word', textAlign: 'center', maxWidth: '180px' }}>
                          {entityData.name}
                        </div>
                      </div>
                      <div style={{ position: 'relative', height: '110px', width: '210px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={entityData.chartData} cx="50%" cy="100%" startAngle={180} endAngle={0} innerRadius={75} outerRadius={100} paddingAngle={0} cornerRadius={10} dataKey="value" stroke="none">
                              {entityData.chartData.map((entry: any, index: number) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ position: 'absolute', bottom: '0', left: '50%', transform: 'translateX(-50%)', textAlign: 'center', width: '100%' }}>
                          <span style={{ fontSize: '1.6rem', fontWeight: 700, color: entityData.statusColor || '#10b981' }}>
                            {entityData.mandate === 0 ? '-' : `${entityData.percent.toFixed(1)}%`}
                          </span>
                        </div>
                      </div>
                      <div style={{ marginTop: '0.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 600, color: entityData.statusColor || '#10b981' }}>{formatCurrency(entityData.utilised)}</div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>of {formatCurrency(entityData.mandate)} utilised</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Admin Expenses Card */}
            <div className="card pdf-section" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2rem', borderBottom: '1px solid var(--border-light)', paddingBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>
                  Admin Expenses
                </h3>
                <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'none', letterSpacing: 'normal' }}>
                  Total: {formatCurrency(adminExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0))} / {formatCurrency((Number(companyProfile?.admin_budget) || 0) + companyEntities.reduce((sum, e) => sum + (Number(e.admin_budget) || 0), 0))}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
                {adminExpenseVisualData.length > 0 ? adminExpenseVisualData.map(entityData => (
                  <div key={entityData.id} style={{ textAlign: 'center', flex: '1', minWidth: '160px', maxWidth: '260px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', wordWrap: 'break-word', textAlign: 'center', maxWidth: '180px' }}>
                        {entityData.name}
                      </div>
                    </div>
                    <div style={{ position: 'relative', height: '110px', width: '210px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={entityData.chartData} cx="50%" cy="100%" startAngle={180} endAngle={0} innerRadius={75} outerRadius={100} paddingAngle={0} cornerRadius={10} dataKey="value" stroke="none">
                            {entityData.chartData.map((entry: any, index: number) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ position: 'absolute', bottom: '0', left: '50%', transform: 'translateX(-50%)', textAlign: 'center', width: '100%' }}>
                        <span style={{ fontSize: '1.6rem', fontWeight: 700, color: '#10b981' }}>
                          {entityData.budget === 0 ? '-' : `${entityData.percent.toFixed(1)}%`}
                        </span>
                      </div>
                    </div>
                    <div style={{ marginTop: '0.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 600, color: entityData.statusColor || '#10b981' }}>{formatCurrency(entityData.utilised)}</div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>of {formatCurrency(entityData.budget)} utilised</div>
                    </div>
                  </div>
                )) : (
                  <div style={{ width: '100%', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    No Admin Budgets configured.
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Foundation Utilisation Section - NEW */}
        {companyProfile?.setup_type !== 'single' && (
          <div className="card pdf-section" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1.5rem', borderBottom: '1px solid var(--border-light)', paddingBottom: '1rem' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Utilisation Through Foundation (From Companies to Foundation)
              </h4>
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                Total to Foundation: {formatCurrency(foundationContributions.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0))} / {formatCurrency(
                  companyEntities.reduce((sum, e) => sum + (Number(e.primary_account_budget) || 0), 0)
                )}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-evenly', flexWrap: 'wrap', gap: '1rem' }}>
              {foundationUtilisationVisualData.length > 0 ? foundationUtilisationVisualData.map(entityData => (
                <div key={entityData.id} style={{ textAlign: 'center', flex: '1', minWidth: '160px', maxWidth: '260px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {/* Name Only */}
                  <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', wordWrap: 'break-word', textAlign: 'center', maxWidth: '180px' }}>
                      {entityData.name}
                    </div>
                  </div>

                  {/* Gauge */}
                  <div style={{ position: 'relative', height: '110px', width: '210px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={entityData.chartData}
                          cx="50%"
                          cy="100%"
                          startAngle={180}
                          endAngle={0}
                          innerRadius={75}
                          outerRadius={100}
                          cornerRadius={10}
                          dataKey="value"
                          stroke="none"
                        >
                          {entityData.chartData.map((entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ position: 'absolute', bottom: '0', left: '50%', transform: 'translateX(-50%)', textAlign: 'center', width: '100%' }}>
                      <span style={{ fontSize: '1.6rem', fontWeight: 700, color: entityData.statusColor }}>
                        {entityData.budget === 0 ? '-' : `${entityData.percent.toFixed(1)}% `}
                      </span>
                    </div>
                  </div>

                  {/* Values below gauge */}
                  <div style={{ marginTop: '0.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: entityData.statusColor }}>{formatCurrency(entityData.utilised)}</div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>of {formatCurrency(entityData.budget)} sent</div>
                  </div>
                </div>
              )) : (
                <div style={{ width: '100%', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                  No Foundation Budgets configured.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Direct Utilisation (From Foundation to NGO partners) - NEW SECTION */}
        {companyProfile?.setup_type !== 'single' && entityMandateVisualData.find(e => e.id === 'foundation_direct') && (
          <div className="card pdf-section" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1.5rem', borderBottom: '1px solid var(--border-light)', paddingBottom: '1rem' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {companyProfile?.setup_type === 'single' ? 'Direct Implementation Utilisation' : 'Direct Utilisation (From Foundation to NGO partners)'}
              </h4>
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                {companyProfile?.setup_type === 'single' ? 'Total' : 'Foundation'} Utilised: {formatCurrency(entityMandateVisualData.find(e => e.id === 'foundation_direct')?.utilised || 0)} / {formatCurrency(entityMandateVisualData.find(e => e.id === 'foundation_direct')?.mandate || 0)}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {(() => {
                const entityData = entityMandateVisualData.find(e => e.id === 'foundation_direct')!;
                return (
                  <div key={entityData.id} style={{ textAlign: 'center', width: '260px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {/* Name Only */}
                    <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', wordWrap: 'break-word', textAlign: 'center', maxWidth: '180px' }}>
                        {entityData.name}
                      </div>
                    </div>

                    {/* Gauge */}
                    <div style={{ position: 'relative', height: '150px', width: '210px', overflow: 'hidden' }}>
                      <ResponsiveContainer width="100%" height={230}>
                        <PieChart>
                          {/* Background Track */}
                          <Pie
                            data={[{ value: 1 }]}
                            cx="50%"
                            cy="55%"
                            startAngle={180}
                            endAngle={0}
                            innerRadius={75}
                            outerRadius={100}
                            cornerRadius={10}
                            dataKey="value"
                            stroke="none"
                            fill="#cbd5e1"
                          />
                          {/* Utilised Progress Layer */}
                          <Pie
                            data={[{ value: entityData.utilised / (entityData.mandate || 1) }]}
                            cx="50%"
                            cy="55%"
                            startAngle={180}
                            endAngle={180 - (entityData.utilised / (entityData.mandate || 1)) * 180}
                            innerRadius={75}
                            outerRadius={100}
                            cornerRadius={10}
                            dataKey="value"
                            stroke="none"
                            fill="#10b981"
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ position: 'absolute', top: '74%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                        <span style={{ fontSize: '1.6rem', fontWeight: 700, color: '#10b981' }}>
                          {entityData.mandate === 0 ? '-' : `${entityData.percent.toFixed(1)}% `}
                        </span>
                      </div>
                    </div>

                    {/* Values below gauge */}
                    <div style={{ marginTop: '0.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 600, color: entityData.statusColor || '#10b981' }}>{formatCurrency(entityData.utilised)}</div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>of {formatCurrency(entityData.mandate)} utilised</div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* CSR Ecosystem / Flowchart - Hidden as requested */}
        {false && (
          <div className="card" style={{ marginBottom: '2rem', padding: '1.5rem', overflowX: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem', paddingBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)' }}>
                CSR Ecosystem
              </h3>
            </div>

            <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center', minWidth: '950px', padding: '1rem 0' }}>

              {/* Left Column: Entities - Only show if not single company */}
              {companyProfile?.setup_type !== 'single' && (
                <div style={{ display: 'flex', position: 'relative', paddingRight: '2.5rem' }}>
                  {/* Vertical text */}
                  <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', background: '#ffe4e6', color: '#881337', padding: '1rem 0.25rem', fontSize: '0.875rem', fontWeight: 600, borderRadius: '4px', marginRight: '1rem', textAlign: 'center', alignSelf: 'center', height: '100px' }}>
                    Companies
                  </div>

                  {/* List of entities */}
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '2rem', position: 'relative', paddingRight: '1rem', borderRight: '2px solid #64748b' }}>
                    {companyEntities.map((entity, i) => {
                      const logoUrl = signedLogoUrls[entity.logo_url] || entity.logo_url;
                      return (
                        <div key={entity.id} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '160px', height: '36px' }}>
                            {logoUrl && !logoErrors[entity.id] ? (
                              <img src={logoUrl} alt={entity.name} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} onError={() => setLogoErrors(prev => ({ ...prev, [entity.id]: true }))} />
                            ) : (
                              <span style={{ fontWeight: 600, color: 'var(--text)' }}>{entity.name}</span>
                            )}
                          </div>
                          {/* Connector line protruding right */}
                          <div style={{ position: 'absolute', top: '50%', right: '-1rem', width: '1rem', height: '2px', background: '#64748b' }} />
                          {/* Little Arrow Head */}
                          <div style={{ position: 'absolute', top: '50%', right: '-1rem', transform: 'translate(50%, -50%)', width: '0', height: '0', borderTop: '4px solid transparent', borderBottom: '4px solid transparent', borderLeft: '6px solid #64748b' }} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Middle Column: Foundation */}
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', position: 'relative', margin: '0 2rem' }}>
                {/* Incoming main line from Left - Only show if not single company */}
                {companyProfile?.setup_type !== 'single' && (
                  <>
                    <div style={{ position: 'absolute', left: '-2rem', top: '50%', width: '2rem', height: '2px', background: '#64748b' }} />
                    <div style={{ position: 'absolute', left: '0', top: '50%', transform: 'translate(-50%, -50%)', width: '0', height: '0', borderTop: '4px solid transparent', borderBottom: '4px solid transparent', borderLeft: '6px solid #64748b' }} />
                  </>
                )}

                <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow)', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '220px', zIndex: 10 }}>
                  <div style={{ height: '40px', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {(signedLogoUrls[companyProfile?.logo_url] || companyProfile?.logo_url) ? (
                      <img src={signedLogoUrls[companyProfile?.logo_url] || companyProfile?.logo_url} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                    ) : (
                      <span style={{ fontWeight: 700, fontSize: '1.25rem', color: 'var(--text)' }}>{companyName}</span>
                    )}
                  </div>
                  <div style={{ background: '#ffe4e6', color: '#881337', padding: '0.5rem 1rem', borderRadius: '4px', fontSize: '0.875rem', fontWeight: 600, textAlign: 'center' }}>
                    {companyProfile?.setup_type === 'single' ? 'Direct Implementation' : 'CSR Implementing Agency'}
                  </div>
                </div>

                {/* Outgoing main line to Right */}
                <div style={{ position: 'absolute', right: '-2rem', top: '50%', width: '2rem', height: '2px', background: '#64748b' }} />
              </div>

              {/* Right Column: Projects & NGOs */}
              <div style={{ display: 'flex', position: 'relative', paddingLeft: '2.5rem' }}>
                {/* List of projects */}
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '2rem', position: 'relative', paddingLeft: '1rem', borderLeft: '2px solid #64748b' }}>
                  {(() => {
                    const uniqueNgosMap = new Map();
                    projects.forEach(p => {
                      const pNgo = (p as any).ngo;
                      if (pNgo && !uniqueNgosMap.has(pNgo.id)) {
                        uniqueNgosMap.set(pNgo.id, pNgo);
                      }
                    });
                    const uniqueNgos = Array.from(uniqueNgosMap.values());

                    if (uniqueNgos.length === 0) {
                      return <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', fontStyle: 'italic', paddingLeft: '1rem' }}>No partners configured yet.</div>;
                    }

                    return uniqueNgos.map((pNgo: any) => (
                      <div key={pNgo.id} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {/* Connector line protruding left */}
                        <div style={{ position: 'absolute', top: '50%', left: '-1rem', width: '1rem', height: '2px', background: '#64748b' }} />
                        {/* Little Arrow Head */}
                        <div style={{ position: 'absolute', top: '50%', left: '0', transform: 'translate(-50%, -50%)', width: '0', height: '0', borderTop: '4px solid transparent', borderBottom: '4px solid transparent', borderLeft: '6px solid #64748b' }} />

                        <div style={{ color: '#64748b', fontWeight: 600, fontSize: '0.875rem' }}>&lt; &gt;</div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '150px', height: '36px' }}>
                          {(signedLogoUrls[pNgo.logo_url] || pNgo.logo_url) && !logoErrors[pNgo.id] ? (
                            <img src={signedLogoUrls[pNgo.logo_url] || pNgo.logo_url} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', objectPosition: 'left' }} onError={() => setLogoErrors(prev => ({ ...prev, [pNgo.id]: true }))} />
                          ) : (
                            <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text)' }}>{pNgo.legal_name}</span>
                          )}
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                {/* Horizontal text block */}
                <div style={{ display: 'flex', alignItems: 'center', marginLeft: '1.5rem' }}>
                  <div style={{ background: '#ffe4e6', color: '#881337', padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 600, borderRadius: '4px', textAlign: 'center', maxWidth: '160px' }}>
                    Project / Implementation Partners
                  </div>
                </div>
              </div>

            </div>
          </div>
        )}


        {/* Subsidiary Company Compliance Management Section - Only show if not single company */}
        {companyProfile?.setup_type !== 'single' && (
          <div style={{ marginBottom: '4rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>Company-wise Compliance Management</h3>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-light)' }}></div>
            </div>

            {/* Entity Visuals */}
            <div className="card pdf-section" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-secondary)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Compliance Status (%)
                </h4>
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', fontWeight: 600, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#10b981' }}></div>
                    <span style={{ color: 'var(--text-secondary)' }}>Completed</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#facc15' }}></div>
                    <span style={{ color: 'var(--text-secondary)' }}>In Progress</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#cbd5e1' }}></div>
                    <span style={{ color: 'var(--text-secondary)' }}>Pending</span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-evenly', flexWrap: 'wrap', gap: '1rem' }}>
                {companyComplianceVisualData.entities.length > 0 ? companyComplianceVisualData.entities.map(companyData => (
                  <div key={companyData.id} style={{ textAlign: 'center', flex: '1', minWidth: '160px', maxWidth: '260px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', wordWrap: 'break-word', textAlign: 'center', maxWidth: '180px' }}>
                        {companyData.name}
                      </div>
                    </div>
                    <div style={{ position: 'relative', height: '150px', width: '210px', overflow: 'hidden' }}>
                      <ResponsiveContainer width="100%" height={230}>
                        <PieChart>
                          {/* Background Track */}
                          <Pie
                            data={[{ value: 1 }]}
                            cx="50%"
                            cy="55%"
                            startAngle={180}
                            endAngle={0}
                            innerRadius={75}
                            outerRadius={100}
                            cornerRadius={10}
                            dataKey="value"
                            stroke="none"
                            fill="#cbd5e1"
                          />
                          {/* In Progress + Completed Layer */}
                          <Pie
                            data={[{ value: (companyData.uploadedCount + companyData.inProgressCount) / (companyData.total || 1) }]}
                            cx="50%"
                            cy="55%"
                            startAngle={180}
                            endAngle={180 - ((companyData.uploadedCount + companyData.inProgressCount) / (companyData.total || 1)) * 180}
                            innerRadius={75}
                            outerRadius={100}
                            cornerRadius={10}
                            dataKey="value"
                            stroke="none"
                            fill="#facc15"
                          />
                          {/* Completed Layer */}
                          <Pie
                            data={[{ value: companyData.uploadedCount / (companyData.total || 1) }]}
                            cx="50%"
                            cy="55%"
                            startAngle={180}
                            endAngle={180 - (companyData.uploadedCount / (companyData.total || 1)) * 180}
                            innerRadius={75}
                            outerRadius={100}
                            cornerRadius={10}
                            dataKey="value"
                            stroke="none"
                            fill="#10b981"
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ position: 'absolute', top: '74%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                        <span style={{ fontSize: '1.6rem', fontWeight: 700, color: '#10b981' }}>
                          {companyData.total === 0 ? '-' : `${companyData.percent.toFixed(2)}% `}
                        </span>
                      </div>
                    </div>
                    <div style={{ marginTop: '0.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text)' }}>{companyData.uploadedCount}</div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginTop: '0.25rem' }}>Completed Documents</div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                        {companyData.inProgressCount} In Progress | {companyData.pendingCount} Pending
                      </div>
                    </div>
                  </div>
                )) : (
                  <div style={{ width: '100%', textAlign: 'center', padding: '1rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                    No other companies configured.
                  </div>
                )}
              </div>
            </div>

            {/* Entity Table */}
            <div className="card pdf-section" style={{ overflow: 'hidden' }}>
              <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', borderBottom: '1px solid var(--border-light)', backgroundColor: '#f5f1f0' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>Company Compliance: Documents</h2>
                <div className="filter-group" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', width: 'auto' }}>
                  <label htmlFor="company-compliance-filter" style={{ marginBottom: 0, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>Filter by Company:</label>
                  <select
                    id="company-compliance-filter"
                    value={selectedComplianceCompany}
                    onChange={(e) => setSelectedComplianceCompany(e.target.value)}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--border-light)', backgroundColor: 'white', maxWidth: '300px', minWidth: '200px', fontFamily: 'inherit' }}
                  >
                    <option value="all">All Companies</option>
                    {companyEntities.map(entity => (
                      <option key={entity.id} value={entity.id}>{entity.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ overflowX: 'auto', padding: '1rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--primary-dark)' }}>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Company</th>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Document</th>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Status</th>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Remarks</th>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Validity</th>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllEntityDocs ? companyDocsTableData.entityDocs : companyDocsTableData.entityDocs.slice(0, 4)).map(row => {
                      let isExpired = false;
                      let isExpiringSoon = false;
                      if (row.rawExpiry) {
                        const daysLeft = Math.ceil((new Date(row.rawExpiry).getTime() - Date.now()) / (1000 * 3600 * 24));
                        if (daysLeft <= 0) isExpired = true;
                        else if (daysLeft <= 15) isExpiringSoon = true;
                      }
                      const isHighlight = isExpired || isExpiringSoon;
                      const bgColor = isHighlight ? '#fee2e2' : 'white';
                      const textColor = isHighlight ? '#991b1b' : 'var(--text)';

                      return (
                        <tr key={row.id} style={{ borderBottom: '1px solid var(--border-light)', backgroundColor: bgColor }}>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>{row.companyName}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>
                            {row.documentName}
                            {isExpired && <span style={{ marginLeft: '8px', padding: '2px 6px', fontSize: '0.7rem', backgroundColor: '#ef4444', color: 'white', borderRadius: '4px' }}>Expired</span>}
                            {isExpiringSoon && <span style={{ marginLeft: '8px', padding: '2px 6px', fontSize: '0.7rem', backgroundColor: '#f59e0b', color: 'white', borderRadius: '4px' }}>Expires Soon</span>}
                          </td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>{row.status}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>{row.remarks}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor, fontWeight: isHighlight ? 700 : 400 }}>{row.validity}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>
                            {row.fileUrl ? (
                              <button onClick={() => window.open(row.fileUrl, '_blank')} className="btn btn-primary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: isHighlight ? '#b91c1c' : '' }}>View</button>
                            ) : null}
                          </td>
                        </tr>
                      )
                    })}
                    {companyDocsTableData.entityDocs.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No entity documents found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {companyDocsTableData.entityDocs.length > 4 && (
                  <div style={{ padding: '1rem', textAlign: 'center', borderTop: '1px solid var(--border-light)' }}>
                    <button
                      onClick={() => setShowAllEntityDocs(!showAllEntityDocs)}
                      className="btn btn-outline"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1.5rem', borderRadius: '20px' }}
                    >
                      {showAllEntityDocs ? 'See Less' : `See More(${companyDocsTableData.entityDocs.length - 4} more)`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}



        {/* Company / Foundation Compliance Management */}
        {foundationComplianceVisualData && (
          <div className="pdf-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                {companyProfile?.setup_type === 'single' ? 'Company Compliance Status' : 'Foundation Compliance Status'}
              </h3>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-light)' }}></div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', alignItems: 'stretch' }}>
              <div className="card pdf-section" style={{ flex: '1 1 300px', maxWidth: '350px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center', borderBottom: '1px solid var(--border-light)', backgroundColor: 'transparent' }}>
                  <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', margin: 0, textAlign: 'center' }}>
                    Company Compliance Status
                  </h2>
                </div>
                
                <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, justifyContent: 'flex-start' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.7rem', fontWeight: 600, flexWrap: 'nowrap', justifyContent: 'center', marginBottom: '1rem', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#10b981' }}></div>
                      <span style={{ color: 'var(--text-secondary)' }}>Completed</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#facc15' }}></div>
                      <span style={{ color: 'var(--text-secondary)' }}>In Progress</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#cbd5e1' }}></div>
                      <span style={{ color: 'var(--text-secondary)' }}>Pending</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center', width: '260px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ marginBottom: '0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', wordWrap: 'break-word', textAlign: 'center', maxWidth: '180px' }}>
                      {foundationComplianceVisualData.name}
                    </div>
                  </div>
                  <div style={{ position: 'relative', height: '150px', width: '210px', overflow: 'hidden' }}>
                    <ResponsiveContainer width="100%" height={230}>
                      <PieChart>
                        {/* Background Track */}
                        <Pie
                          data={[{ value: 1 }]}
                          cx="50%"
                          cy="55%"
                          startAngle={180}
                          endAngle={0}
                          innerRadius={75}
                          outerRadius={100}
                          cornerRadius={10}
                          dataKey="value"
                          stroke="none"
                          fill="#cbd5e1"
                        />
                        {/* In Progress + Completed Layer */}
                        <Pie
                          data={[{ value: (foundationComplianceVisualData.uploadedCount + foundationComplianceVisualData.inProgressCount) / (foundationComplianceVisualData.total || 1) }]}
                          cx="50%"
                          cy="55%"
                          startAngle={180}
                          endAngle={180 - ((foundationComplianceVisualData.uploadedCount + foundationComplianceVisualData.inProgressCount) / (foundationComplianceVisualData.total || 1)) * 180}
                          innerRadius={75}
                          outerRadius={100}
                          cornerRadius={10}
                          dataKey="value"
                          stroke="none"
                          fill="#facc15"
                        />
                        {/* Completed Layer */}
                        <Pie
                          data={[{ value: foundationComplianceVisualData.uploadedCount / (foundationComplianceVisualData.total || 1) }]}
                          cx="50%"
                          cy="55%"
                          startAngle={180}
                          endAngle={180 - (foundationComplianceVisualData.uploadedCount / (foundationComplianceVisualData.total || 1)) * 180}
                          innerRadius={75}
                          outerRadius={100}
                          cornerRadius={10}
                          dataKey="value"
                          stroke="none"
                          fill="#10b981"
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ position: 'absolute', top: '74%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                      <span style={{ fontSize: '1.6rem', fontWeight: 700, color: '#10b981' }}>
                        {foundationComplianceVisualData.total === 0 ? '-' : `${foundationComplianceVisualData.percent.toFixed(2)}% `}
                      </span>
                    </div>
                  </div>
                  <div style={{ marginTop: '0.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text)' }}>{foundationComplianceVisualData.uploadedCount}</div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginTop: '0.25rem' }}>Completed Documents</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                      {foundationComplianceVisualData.inProgressCount} In Progress | {foundationComplianceVisualData.pendingCount} Pending
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Foundation Docs Table */}
          <div className="card" style={{ overflow: 'hidden', flex: '2 1 600px' }}>
              <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', borderBottom: '1px solid var(--border-light)', backgroundColor: 'transparent' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                  {companyProfile?.setup_type === 'single' ? 'Company Compliance Documents' : 'Foundation Compliance Documents'}
                </h2>
              </div>
              <div style={{ overflowX: 'auto', padding: '1rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--primary-dark)' }}>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Document</th>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Status</th>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Remarks</th>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Validity</th>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllFoundationDocs ? companyDocsTableData.foundationDocs : companyDocsTableData.foundationDocs.slice(0, 4)).map(row => {
                      let isExpired = false;
                      let isExpiringSoon = false;
                      if (row.rawExpiry) {
                        const daysLeft = Math.ceil((new Date(row.rawExpiry).getTime() - Date.now()) / (1000 * 3600 * 24));
                        if (daysLeft <= 0) isExpired = true;
                        else if (daysLeft <= 15) isExpiringSoon = true;
                      }
                      const isHighlight = isExpired || isExpiringSoon;
                      const bgColor = isHighlight ? '#fee2e2' : 'white';
                      const textColor = isHighlight ? '#991b1b' : 'var(--text)';

                      return (
                        <tr key={row.id} style={{ borderBottom: '1px solid var(--border-light)', backgroundColor: bgColor }}>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>
                            {row.documentName}
                            {isExpired && <span style={{ marginLeft: '8px', padding: '2px 6px', fontSize: '0.7rem', backgroundColor: '#ef4444', color: 'white', borderRadius: '4px' }}>Expired</span>}
                            {isExpiringSoon && <span style={{ marginLeft: '8px', padding: '2px 6px', fontSize: '0.7rem', backgroundColor: '#f59e0b', color: 'white', borderRadius: '4px' }}>Expires Soon</span>}
                          </td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>{row.status}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>{row.remarks}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor, fontWeight: isHighlight ? 700 : 400 }}>{row.validity}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>
                            {row.fileUrl ? (
                              <button onClick={() => window.open(row.fileUrl, '_blank')} className="btn btn-primary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: isHighlight ? '#b91c1c' : '' }}>View</button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                    {companyDocsTableData.foundationDocs.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                          {companyProfile?.setup_type === 'single' ? 'No company documents found.' : 'No foundation documents found.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {companyDocsTableData.foundationDocs.length > 4 && (
                  <div style={{ padding: '1rem', textAlign: 'center', borderTop: '1px solid var(--border-light)' }}>
                    <button
                      onClick={() => setShowAllFoundationDocs(!showAllFoundationDocs)}
                      className="btn btn-outline"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1.5rem', borderRadius: '20px' }}
                    >
                      {showAllFoundationDocs ? 'See Less' : `See More(${companyDocsTableData.foundationDocs.length - 4} more)`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        )}


        {/* NGO Compliance Visuals & Table */}
        {ngoComplianceVisualData.length > 0 && ngos.length > 0 && (
          <div className="pdf-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                Partner Compliance Status
              </h3>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-light)' }}></div>
            </div>
            
            <div style={companyProfile?.setup_type === 'single' ? { display: 'flex', flexWrap: 'wrap', gap: '2rem', alignItems: 'stretch', marginBottom: '2rem' } : {}}>
            <div className={`card pdf-section`} style={companyProfile?.setup_type === 'single' ? { flex: '1 1 300px', maxWidth: '350px', overflow: 'hidden', display: 'flex', flexDirection: 'column' } : { padding: '2rem', marginBottom: '2rem' }}>
              {companyProfile?.setup_type === 'single' ? (
                <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center', borderBottom: '1px solid var(--border-light)', backgroundColor: 'transparent' }}>
                  <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', margin: 0, textAlign: 'center' }}>
                    Partner Compliance Status
                  </h2>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
                  <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>Compliance Completion (%) by NGO</h2>
                  <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', fontWeight: 600, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#10b981' }}></div>
                      <span style={{ color: 'var(--text-secondary)' }}>Completed</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#facc15' }}></div>
                      <span style={{ color: 'var(--text-secondary)' }}>In Progress</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#cbd5e1' }}></div>
                      <span style={{ color: 'var(--text-secondary)' }}>Pending</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div style={companyProfile?.setup_type === 'single' ? { padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, justifyContent: 'flex-start' } : { display: 'flex', justifyContent: 'space-evenly', flexWrap: 'wrap', gap: '1rem' }}>
                {companyProfile?.setup_type === 'single' && (
                  <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.7rem', fontWeight: 600, flexWrap: 'nowrap', justifyContent: 'center', marginBottom: '1rem', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#10b981' }}></div>
                      <span style={{ color: 'var(--text-secondary)' }}>Completed</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#facc15' }}></div>
                      <span style={{ color: 'var(--text-secondary)' }}>In Progress</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#cbd5e1' }}></div>
                      <span style={{ color: 'var(--text-secondary)' }}>Pending</span>
                    </div>
                  </div>
                )}
                {ngoComplianceVisualData.map(ngoData => (
                  <div key={ngoData.id} style={companyProfile?.setup_type === 'single' ? { textAlign: 'center', width: '260px', display: 'flex', flexDirection: 'column', alignItems: 'center' } : { textAlign: 'center', width: '220px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={companyProfile?.setup_type === 'single' ? { marginBottom: '0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' } : { marginBottom: '0.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', height: '60px', justifyContent: 'flex-end' }}>
                      {(!companyProfile || companyProfile.setup_type !== 'single') && ((signedLogoUrls[(ngoData as any).logo_url] || (ngoData as any).logo_url) && !logoErrors[ngoData.id] ? (
                        <img
                          src={signedLogoUrls[(ngoData as any).logo_url] || (ngoData as any).logo_url}
                          alt={`${ngoData.name} logo`}
                          style={{ height: '36px', maxWidth: '120px', objectFit: 'contain' }}
                          onError={() => setLogoErrors(prev => ({ ...prev, [ngoData.id]: true }))}
                        />
                      ) : (
                        <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', marginBottom: '0', textAlign: 'center' }}>
                          {ngoData.name.length > 15 ? ngoData.name.substring(0, 15) + '...' : ngoData.name}
                        </h4>
                      ))}
                      {companyProfile?.setup_type === 'single' && (
                         <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', wordWrap: 'break-word', textAlign: 'center', maxWidth: '180px' }}>
                           {ngoData.name}
                         </div>
                      )}
                    </div>
                    <div style={companyProfile?.setup_type === 'single' ? { position: 'relative', height: '150px', width: '210px', overflow: 'hidden' } : { position: 'relative', height: '160px', width: '220px', overflow: 'hidden' }}>
                      <ResponsiveContainer width="100%" height={companyProfile?.setup_type === 'single' ? 230 : 240}>
                        <PieChart>
                          <Pie data={[{ value: 1 }]} cx="50%" cy="55%" startAngle={180} endAngle={0} innerRadius={companyProfile?.setup_type === 'single' ? 75 : 80} outerRadius={companyProfile?.setup_type === 'single' ? 100 : 105} cornerRadius={10} dataKey="value" stroke="none" fill="#cbd5e1" />
                          <Pie data={[{ value: (ngoData.uploadedCount + ngoData.inProgressCount) / (ngoData.total || 1) }]} cx="50%" cy="55%" startAngle={180} endAngle={180 - ((ngoData.uploadedCount + ngoData.inProgressCount) / (ngoData.total || 1)) * 180} innerRadius={companyProfile?.setup_type === 'single' ? 75 : 80} outerRadius={companyProfile?.setup_type === 'single' ? 100 : 105} cornerRadius={10} dataKey="value" stroke="none" fill="#facc15" />
                          <Pie data={[{ value: ngoData.uploadedCount / (ngoData.total || 1) }]} cx="50%" cy="55%" startAngle={180} endAngle={180 - (ngoData.uploadedCount / (ngoData.total || 1)) * 180} innerRadius={companyProfile?.setup_type === 'single' ? 75 : 80} outerRadius={companyProfile?.setup_type === 'single' ? 100 : 105} cornerRadius={10} dataKey="value" stroke="none" fill="#10b981" />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={companyProfile?.setup_type === 'single' ? { position: 'absolute', top: '74%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' } : { position: 'absolute', top: '75%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                        <span style={companyProfile?.setup_type === 'single' ? { fontSize: '1.6rem', fontWeight: 700, color: '#10b981' } : { fontSize: '2rem', fontWeight: 400, color: '#10b981' }}>
                          {companyProfile?.setup_type === 'single' && ngoData.total === 0 ? '-' : `${ngoData.percent.toFixed(2)}%`}
                        </span>
                      </div>
                    </div>
                    <div style={companyProfile?.setup_type === 'single' ? { marginTop: '0.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center' } : { marginTop: '0' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text)' }}>{ngoData.uploadedCount}</div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginTop: '0.25rem' }}>Completed Documents</div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                        {ngoData.inProgressCount} In Progress | {ngoData.pendingCount} Pending
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* NGO Compliance Table */}
            <div className="card pdf-section" style={companyProfile?.setup_type === 'single' ? { overflow: 'hidden', flex: '2 1 600px' } : { marginBottom: '2rem', overflow: 'hidden' }}>
              <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', borderBottom: '1px solid var(--border-light)', backgroundColor: companyProfile?.setup_type === 'single' ? 'transparent' : '#f5f1f0' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>CSR Compliance By Partners: Documents</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <label style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>NGO:</label>
                  <select
                    value={selectedComplianceNgo}
                    onChange={(e) => setSelectedComplianceNgo(e.target.value)}
                    style={{ padding: '0.5rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', minWidth: '180px', backgroundColor: 'white', cursor: 'pointer', fontSize: '0.875rem' }}
                  >
                    <option value="all">All ({ngos.length})</option>
                    {ngos.map(ngo => (
                      <option key={ngo.id} value={ngo.id}>{ngo.legal_name.length > 20 ? ngo.legal_name.substring(0, 20) + '...' : ngo.legal_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', padding: '1rem' }}>
                <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--primary-dark)' }}>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>NGO</th>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Document</th>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Status</th>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Remarks</th>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Validity</th>
                      <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllNgoDocs ? ngoDocsTableData : ngoDocsTableData.slice(0, 4)).map(row => {
                      let isExpired = false;
                      let isExpiringSoon = false;
                      if (row.rawExpiry) {
                        const daysLeft = Math.ceil((new Date(row.rawExpiry).getTime() - Date.now()) / (1000 * 3600 * 24));
                        if (daysLeft <= 0) isExpired = true;
                        else if (daysLeft <= 15) isExpiringSoon = true;
                      }
                      const isHighlight = isExpired || isExpiringSoon;
                      const bgColor = isHighlight ? '#fee2e2' : 'white';
                      const textColor = isHighlight ? '#991b1b' : 'var(--text)';
                      return (
                        <tr key={row.id} style={{ borderBottom: '1px solid var(--border-light)', backgroundColor: bgColor }}>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>{row.ngoName}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>
                            {row.documentName}
                            {isExpired && <span style={{ marginLeft: '8px', padding: '2px 6px', fontSize: '0.7rem', backgroundColor: '#ef4444', color: 'white', borderRadius: '4px' }}>Expired</span>}
                            {isExpiringSoon && <span style={{ marginLeft: '8px', padding: '2px 6px', fontSize: '0.7rem', backgroundColor: '#f59e0b', color: 'white', borderRadius: '4px' }}>Expires Soon</span>}
                          </td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>{row.status}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>{row.remarks}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor, fontWeight: isHighlight ? 700 : 400 }}>{row.validity}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>
                            {row.fileUrl ? (
                              <button onClick={() => window.open(row.fileUrl, '_blank')} className="btn btn-primary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: isHighlight ? '#b91c1c' : '' }}>View</button>
                            ) : null}
                          </td>
                        </tr>
                      )
                    })}
                    {ngoDocsTableData.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No documents found for this selection.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {ngoDocsTableData.length > 4 && (
                  <div style={{ padding: '1rem', textAlign: 'center', borderTop: '1px solid var(--border-light)' }}>
                    <button
                      onClick={() => setShowAllNgoDocs(!showAllNgoDocs)}
                      className="btn btn-outline"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1.5rem', borderRadius: '20px' }}
                    >
                      {showAllNgoDocs ? 'See Less' : `See More(${ngoDocsTableData.length - 4} more)`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
        )}

        {/* Charts */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>


          {projectBudgetVsUtilisedData.length > 0 && (
            <div className="card pdf-section" style={{ padding: '1.25rem 1rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#10b981' }}>
                Project-wise Budget vs. Utilised
              </h3>
              <div style={{ width: '100%' }}>
                <ResponsiveContainer width="100%" height={Math.max(260, projectBudgetVsUtilisedData.length * 55 + 70)}>
                  <BarChart data={projectBudgetVsUtilisedData} layout="vertical" margin={{ top: 10, right: 15, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={formatShortINR} tick={{ fontSize: 10, fill: '#64748b' }} />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={125}
                      tick={<CustomYAxisTick />}
                      interval={0}
                    />
                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    <Legend wrapperStyle={{ fontSize: '0.8rem', paddingTop: '8px' }} />
                    <Bar dataKey="Budget" fill="#fbbf24" radius={[0, 4, 4, 0]} barSize={14} />
                    <Bar dataKey="Utilised" fill="#10b981" radius={[0, 4, 4, 0]} barSize={14} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Metrics Scorecards */}
          {metrics.length > 0 && (
            <div className="card" style={{ padding: '2rem 1.5rem', marginBottom: '0rem', height: 'fit-content' }}>
              <div className="pdf-section" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0, color: 'var(--text)' }}>
                  Project-wise Impact Metrics
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', width: 'auto' }}>
                  <label style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap' }}>Filter by NGO:</label>
                  <select
                    value={selectedImpactNgo}
                    onChange={(e) => setSelectedImpactNgo(e.target.value)}
                    style={{ padding: '0.4rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', maxWidth: '300px', minWidth: '200px', backgroundColor: 'white', cursor: 'pointer', fontSize: '0.875rem', fontFamily: 'inherit' }}
                  >
                    <option value="all">All NGOs ({ngos.length})</option>
                    {ngos.map(ngo => (
                      <option key={ngo.id} value={ngo.id}>{ngo.legal_name.length > 20 ? ngo.legal_name.substring(0, 20) + '...' : ngo.legal_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                {(() => {
                  const filteredImpactMetrics = metrics.filter(m => {
                    const project = projects.find(p => p.id === m.project_id);
                    const isProjectActive = project?.status === 'active';
                    const matchesNgoFilter = selectedImpactNgo === 'all' || (m.ngo_ids && m.ngo_ids.includes(selectedImpactNgo));
                    return isProjectActive && matchesNgoFilter;
                  });

                  if (filteredImpactMetrics.length === 0) {
                    return (
                      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                        No impact metrics available for active projects.
                      </div>
                    );
                  }

                  return Object.entries(
                    filteredImpactMetrics.reduce((acc, metric) => {
                      let ngo = metric.ngo_name;
                      if (!ngo) {
                        const project = projects.find(p => p.id === metric.project_id);
                        ngo = `${project?.name || 'Direct'} (Direct Implementation)`;
                      }
                      if (!acc[ngo]) acc[ngo] = [];
                      acc[ngo].push(metric);
                      return acc;
                    }, {} as Record<string, typeof metrics>)
                  ).map(([ngoName, ngoMetrics]) => (
                    <div key={ngoName} className="pdf-section" style={{ marginBottom: '1rem', width: '100%' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        marginBottom: '1.25rem',
                        paddingBottom: '0.5rem',
                        borderBottom: '1px solid var(--border-light)'
                      }}>
                        <div style={{
                          width: '4px',
                          height: '18px',
                          backgroundColor: '#10b981',
                          borderRadius: '2px'
                        }}></div>
                        <h4 style={{
                          fontSize: '1rem',
                          fontWeight: 700,
                          color: 'var(--text)',
                          margin: 0
                        }}>
                          {ngoName}
                        </h4>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                        gap: '1.5rem',
                        justifyItems: 'center'
                      }}>
                        {ngoMetrics.map((metric) => {
                          const achieved = metric.current_value || 0;
                          const target = metric.target_value || 0;
                          const progress = target > 0 ? (achieved / target) * 100 : 0;

                          let color = '#9ca3af';
                          if (target > 0) {
                            if (progress >= 80) color = '#10b981';
                            else if (progress >= 30) color = '#f59e0b';
                            else color = '#ef4444';
                          }

                          const displayProgress = Math.min(progress, 100);
                          const radius = 70;
                          const stroke = 18;
                          const normalizedRadius = radius - (stroke / 2);
                          const circumference = normalizedRadius * 2 * Math.PI;
                          const strokeDashoffset = target > 0 ? circumference - (displayProgress / 100) * circumference : circumference;

                          return (
                            <div key={metric.id} style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              textAlign: 'center',
                              width: '100%',
                              maxWidth: '260px'
                            }}>
                              <h5 style={{
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                marginBottom: '0.75rem',
                                color: 'var(--text)',
                                minHeight: '2.2rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: 1.3
                              }}>
                                {metric.metric_name}
                              </h5>

                              <div style={{ position: 'relative', width: radius * 2, height: radius * 2 }}>
                                <svg height={radius * 2} width={radius * 2} style={{ transform: 'rotate(-90deg)' }}>
                                  <circle
                                    stroke="#f3f4f6"
                                    fill="transparent"
                                    strokeWidth={stroke}
                                    r={normalizedRadius}
                                    cx={radius}
                                    cy={radius}
                                  />
                                  <circle
                                    stroke={color}
                                    fill="transparent"
                                    strokeWidth={stroke}
                                    strokeDasharray={circumference + ' ' + circumference}
                                    style={{ strokeDashoffset, transition: 'stroke-dashoffset 1s ease-in-out' }}
                                    strokeLinecap="round"
                                    r={normalizedRadius}
                                    cx={radius}
                                    cy={radius}
                                  />
                                </svg>

                                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                  <span style={{ fontSize: '1.6rem', fontWeight: 700, color, lineHeight: 1 }}>
                                    {target > 0 ? `${progress.toFixed(0)}% ` : '--'}
                                  </span>
                                  <span style={{ fontSize: '1.15rem', fontWeight: 600, color: 'var(--text)', marginTop: '0.2rem', lineHeight: 1 }}>
                                    {achieved.toLocaleString('en-IN')}
                                  </span>
                                  <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.15rem', fontWeight: 500 }}>
                                    of {target > 0 ? target.toLocaleString('en-IN') : '--'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CompanyDashboard;
